import { Pid as TPid, TransportStream } from '../../tr101Types'
import { ApplianceConfiguration as TApplianceConfiguration } from '../../messages'
import {
    type AudioLanguageAudioType,
    ChannelState,
    DelayMode,
    RistInputMultipathState,
    RistOutputMultipathState,
    type SrtStats,
    UdpInputStatusCode,
    UdpOutputStatusCode,
    SdiInputStatusCode,
    SdiOutputStatusCode,
    SdiOutputLastFailedStage,
    AsiInputStatusCode,
    AsiOutputStatusCode,
} from '../../rist'
import { RtmpOutputState } from '../../rtmp'
import { SrtConnectionStatus } from '../../srt'
import { K8sService } from '../../k8s/services'
import { KubernetesRoles } from '../../k8s/labels'
import {
    ApplianceAdPlaylist,
    type ConnectitProductId,
    type NimbraEdgeProductId,
    PortDirection,
    type Product,
} from '../../types'
import { OmitFromUnion } from 'common/util'
import { IncomingMessage, ServerResponse } from 'http'
import { ApplianceCapabilities, PlatformMetadata } from '../model/applianceRegistration'

export {
    RistInputMultipathState,
    RistOutputMultipathState,
    UdpOutputStatusCode,
    SdiInputStatusCode,
    SdiOutputStatusCode,
    SdiOutputLastFailedStage,
    AsiInputStatusCode,
    AsiOutputStatusCode,
}
export { DelayMode, audioLanguageAudioTypes } from '../../rist'
export type { AudioLanguageAudioType }
export type { Product, ConnectitProductId, NimbraEdgeProductId }
export type Pid = TPid
export type ApplianceConfiguration = TApplianceConfiguration
export type * from './internal/region'
import type {
    FrameformerQualityTier as FrameformerQualityTierValue,
    ServiceType as ServiceTypeValue,
} from '../../constants'

/** @OAS_NAME FrameformerQualityTier */
export type FrameformerQualityTier = FrameformerQualityTierValue

/** @OAS_NAME ServiceType */
export type ServiceType = ServiceTypeValue

export interface SortOrder<T extends string = string> {
    field: T
    descending: boolean
}

export enum LogLevel {
    fatal = 0,
    error = 1,
    warn = 2,
    info = 3,
    debug = 4,
    trace = 5,
}

export enum EdgeProduct {
    edge = 'edge',
    loci = 'loci',
}

// Key is what we call it in NM to be consistent with LogLevel, and the value is what the ristserver accepts
export enum RistserverLogLevel {
    fatal = 'critical',
    error = 'error',
    warn = 'warning',
    info = 'info',
    debug = 'debug',
    trace = 'trace',
}

export enum RistProfile {
    simple = 'simple',
    main = 'main',
}

export enum SrtMode {
    listener = 'listener',
    caller = 'caller',
    rendezvous = 'rendezvous',
}

export enum ZixiMode {
    pull = 'pull',
    push = 'push',
}

/** @deprecated For srt-live-transmit */
export enum SrtKeylen {
    aes128 = '16',
    aes192 = '24',
    aes256 = '32',
    none = '-1',
}

export enum SrtRateLimiting {
    notEnforced = 'Not enforced',
    absolute = 'Absolute',
    relativeToInput = 'Relative to input',
}

export interface ListResultError {
    message: string
}

export interface ListResult<T> {
    items: T[]
    total: number
    sortOrder?: SortOrder
    skip?: number
    limit?: number
    errors?: ListResultError[]
}

export enum AuditLogOperationResult {
    error = 'error',
    success = 'success',
}

/**
 * @OAS_DESCRIPTION Parameters on which a list of audit-log entries can be filtered
 */
export interface AuditFilter {
    method?: string
    pathname?: string
    operation?: string
    result?: AuditLogOperationResult
    username?: string
    entity?: string
    entityId?: string
    entityName?: string
    fromDate?: Date
    toDate?: Date
    /** Exclude derived operations (those with a parentOperationId) */
    excludeDerived?: boolean
    /** Show only derived operations with this parent */
    parentOperationId?: string
}

export interface AdAuditFilter {
    filter?: string
    fromDate?: Date
    toDate?: Date

    entityTypes?: string[]
    entity?: string
    entityId?: string
    entityName?: string
    entitySize?: number

    methods?: string[]
    results?: string[]
    message?: string
    components?: string[]
    statuses?: string[]

    adBreakIds?: string[]
    eventId?: number
    inputIds?: string[]
    inputNames?: string[]
    streamId?: number
    channelIds?: number[]
    pod?: string
    source?: string
    target?: string
    traceId?: string
    programNumber?: number
    programName?: string
}

export interface AdPodFilter extends SearchableFilter {
    fromDate?: Date
    toDate?: Date
    adCreativeId?: string
    status?: AdPodStatus
}

export interface AdCreativeFilter extends SearchableFilter {
    fromDate?: Date
    toDate?: Date
    status?: AdCreativeStatus
}

export interface AdBreakFilter {
    fromDate?: Date
    toDate?: Date
    status?: AdBreakStatus
    eventId?: number
    adId?: string
    inputId?: string
    programName?: string
    programNumber?: number
    adServerId?: string
}

export enum AuditOperation {
    create = 'create',
    delete = 'delete',
    update = 'update',
    login = 'login',
    logout = 'logout',

    rerouteInput = 'rerouteInput',
    switchInput = 'switchInput',
    tcpdumpInput = 'tcpdumpInput',
    tcpdumpOutput = 'tcpdumpOutput',

    updateInputRecipients = 'updateInputRecipients',
    sendToOutputs = 'sendToOutputs',

    updateApplianceVersion = 'updateApplianceVersion',
    registerAppliance = 'registerAppliance',
    reregisterAppliance = 'reregisterAppliance',
    updateApplianceBasicInfo = 'updateApplianceBasicInfo',
    updateApplianceIpConfig = 'updateApplianceIpConfig',
    restartAppliance = 'restartAppliance',
    recreateApplianceTunnels = 'recreateApplianceTunnels',
    recreateTunnel = 'recreateTunnel',
    updateApplianceImage = 'updateApplianceImage',
    drainAppliance = 'drainAppliance',
    migrateApplianceSerial = 'migrateApplianceSerial',
    // An interface vanished from an appliance's registration but could not be removed because a
    // service (input/output/tunnel/affiliate) still references it — the alarm was raised instead.
    retainInterface = 'retainInterface',
    migrateEdgeOs = 'migrateEdgeOs',

    startCloudApplianceInstance = 'startCloudApplianceInstance',
    stopCloudApplianceInstance = 'stopCloudApplianceInstance',

    beginSystemUpgrade = 'beginSystemUpgrade',
    completeSystemUpgrade = 'completeSystemUpgrade',

    toggle = 'toggle',
}

export interface AuditRefData {
    inputName?: string
    action?: 'enable' | 'disable' | undefined
    hasDataSoftwareVersion?: boolean
    cloudApplianceInstance?: {
        id: string
        name?: string
        region?: string
    }
    systemUpgrade?: {
        fromVersion: string
        toVersion: string
    }
    /** Region name for kubernetes node operations */
    region?: string
    updateIpConfig?: {
        added: string[]
        removed: string[]
        modified: string[]
    }
}

export type AuditChangeType = 'added' | 'removed' | 'changed' | 'sensitive-changed'

export interface AuditFieldChange {
    /** RFC 6902 JSON Pointer path, e.g. "/name", "/ports/0/address" */
    field: string
    /** Previous value — absent for 'added' */
    from?: unknown
    /** New value — absent for 'removed' */
    to?: unknown
    type: AuditChangeType
}

/**
 * @OAS_DESCRIPTION Contains information about actions performed in the system, e.g. who did what and when, etc.
 */
export interface AuditLog {
    id: string
    operation: AuditOperation
    entity: string
    entityId?: string
    entityName: string
    // pathname: string
    // query: string
    userId: string
    username: string
    clientIp?: string // Only visible for super user
    // request?: string
    // response?: string
    error?: string
    executionTimeMs?: number
    createdAt: Date
    updatedAt?: Date
    authtoken?: string
    impersonatorId?: string
    impersonatorUsername?: string

    /**
     * Arbitrary reference data used for generating a textual description
     * of the operation in the audit log
     */
    refData: AuditRefData

    /**
     * For derived/side-effect operations: the ID of the parent operation that triggered this one.
     * When present, this operation was not directly requested by the user but was automatically
     * performed as a consequence of the parent operation.
     */
    parentOperationId?: string

    /**
     * Human-readable explanation of why this derived operation was triggered.
     * Only present when parentOperationId is set.
     * Examples: "Port ownership changed", "Cascade delete"
     */
    derivedReason?: string

    /** Number of derived operations triggered by this operation (0 for derived operations or parents with no children) */
    numDerivedOperations?: number

    /** Field-level changeset for update operations */
    changes?: AuditFieldChange[]
}

export type CloudApplianceAuditLogEntry = Pick<AuditLog, 'operation' | 'entity' | 'entityId' | 'entityName' | 'refData'>

export interface Entity {
    id: string
}

export enum AlarmCause {
    INTERFACE_REMOVED_BUT_IN_USE = 'Interface removed but still in use',
    CORE_APPLIANCE_PUBLIC_IP_MISSING = 'Appliance needs public ip mapping', // Core appliance or external thumb
    CORE_INPUT_INVALID_ADDRESS = 'Core input configured on an address that is not available',
    CORE_OUTPUT_INVALID_ADDRESS = 'Core output configured on an address that is not available',
    FIREWALL_NFT_ERROR = 'Failed to apply firewall settings using nftables',
    MPTS_SUPPORT_ERROR = 'Failed to run mpts demux binary',
    MPTS_STARTUP_ERROR = 'Unknown error when starting the mpts demux binary',
    MPTS_UNEXPECTED_EXIT = 'The mpts demux binary unexpectedly exited',
    MPTS_CALL_TIMEOUT = 'The mpts demux binary did not give a timely response to an api call',
    BACKUP_JOB_FAILED = 'Backup job failed',
    RTMP_RECEIVER_EXITED = 'RTMP receiver exited unexpectedly',
    RTMP_SENDER_EXITED = 'RTMP sender exited unexpectedly',
    TRANSCODE_EXITED = 'Transcode process exited unexpectedly',
    TRANSCODE_MISSING_PID = 'Audio reshuffling failed due to missing PID',
    TRANSCODE_MISALIGNED_STREAMS = 'Stream timestamps are misaligned',
    INPUT_MAX_BITRATE_EXCEEDED = 'Ingested stream traffic exceeds the maximum bitrate',
    INPUT_UNHEALTHY = 'Input is unhealthy',
    OUTPUT_UNHEALTHY = 'Output is unhealthy',
    APPLIANCE_OFFLINE = 'Appliance is offline',
    APPLIANCE_DEFAULT_PASSWORD = 'Appliance is using the default password',
    APPLIANCE_HIGH_CPU_USAGE = 'High CPU usage detected',
    TRANSCODE_ACCELERATOR_HIGH_USAGE = 'High transcode accelerator usage detected',

    // Nimbra 400 alarms
    VA_LOSS_OF_SIGNAL = 'Loss of signal',
    VA_CALL_ESTABLISHMENT_ERROR = 'Call establishment error',
    VA_CONFIGURATION_OR_CUSTOMIZATION_ERROR = 'Configuration or customization error',
    VA_RESOURCE_CAPACITY = 'Resource at or nearing capacity',
    VA_SOFTWARE_PROGRAM_ERROR = 'Software program error',

    // Edge-data alarms
    RISTSERVER_ADD_CHANNEL = 'Failed to add channel',
    RISTSERVER_ADD_INPUT_STREAM_RIST = 'Failed to add input stream rist',
    RISTSERVER_ADD_INPUT_STREAM_RTP = 'Failed to add input stream rtp',
    RISTSERVER_ADD_INPUT_STREAM_SRT = 'Failed to add input stream srt',
    RISTSERVER_ADD_INPUT_STREAM_UDP = 'Failed to add input stream udp',
    RISTSERVER_ADD_INPUT_STREAM_ASI = 'Failed to add input stream asi',
    RISTSERVER_ADD_INPUT_STREAM_ADDRESS_ALREADY_IN_USE = 'Address already in use',
    RISTSERVER_ADD_OUTPUT_STREAM_RIST = 'Failed to add output stream rist',
    RISTSERVER_ADD_OUTPUT_STREAM_RTP = 'Failed to add output stream rtp',
    RISTSERVER_ADD_OUTPUT_STREAM_SRT = 'Failed to add output stream srt',
    RISTSERVER_ADD_OUTPUT_STREAM_UDP = 'Failed to add output stream udp',
    RISTSERVER_ADD_OUTPUT_STREAM_ASI = 'Failed to add output stream asi',
    RISTSERVER_ADD_DERIVED_INPUT = 'Failed to add derived input',
    RISTSERVER_ADD_SRT_LISTENER = 'Failed to add srt listener',
    RISTSERVER_ADD_SRT_CALLER = 'Failed to add srt caller',
    RISTSERVER_ADD_SRT_RENDEZVOUS = 'Failed to add srt rendezvous',
    RISTSERVER_ADD_TUNNEL_CLIENT = 'Failed to add tunnel client',
    RISTSERVER_ADD_TUNNEL_SERVER = 'Failed to add tunnel server',
    RISTSERVER_GET_DELTA_STATISTICS = 'Failed to get delta statistics',
    RISTSERVER_REMOVE_CHANNEL = 'Failed to remove channel',
    RISTSERVER_REMOVE_INPUT_STREAM = 'Failed to remove input stream',
    RISTSERVER_REMOVE_OUTPUT_STREAM = 'Failed to remove output stream',
    RISTSERVER_REMOVE_TUNNEL = 'Failed to remove tunnel',
    RISTSERVER_SET_INPUT_MULTIPATH_PARAMS = 'Failed to set input multipath params',
    RISTSERVER_SET_LOGGING_OPTIONS = 'Failed to set logging options',
    RISTSERVER_SET_OUTPUT_STREAM_MAXBITRATE = 'Failed to set output stream max bitrate',
    RISTSERVER_SET_SDI_BOARD_PROPERTIES = 'Failed to set SDI board properties',
    RISTSERVER_GET_STATISTICS = 'Failed to get statistics',
    RISTSERVER_GET_STATUS = 'Failed to get status',
    RISTSERVER_GET_VERSION = 'Failed to get version',
    RISTSERVER_SET_OUTPUT_STREAM_NEW_AD_PLAYLIST = 'Failed to set ad break playlist',
    RISTSERVER_SET_OUTPUT_STREAM_NOTIFY_SCTE = 'Failed to set output stream notify scte',
    RISTSERVER_SET_OUTPUT_STREAM_AD_INSERTION = 'Failed to set ad insertion',
    RISTSERVER_SET_OUTPUT_STREAM_AD_SCTE_35_SOURCE = 'Failed to set ad insertion SCTE source filter',
    RISTSERVER_SET_OUTPUT_STREAM_AD_DEFAULT_BLACKOUT_PLAYLIST = 'Failed to set default blackout playlist',
    RISTSERVER_SET_OUTPUT_STREAM_AD_DEFAULT_PLAYLIST = 'Failed to set default ad playlist',
    RISTSERVER_SET_AD_FILE_AVAILABILITY = 'Failed to set ad file availability',
    RISTSERVER_SET_OUTPUT_STREAM_AD_SCHEDULE = 'Failed to schedule ad break',
    RISTSERVER_SET_OUTPUT_STREAM_AD_SCHEDULE_END = 'Failed to schedule ad break end',
    RISTSERVER_SET_OUTPUT_STREAM_AD_SCHEDULE_BLACKOUT = 'Failed to schedule blackout start',
    RISTSERVER_SET_OUTPUT_STREAM_AD_SCHEDULE_BLACKOUT_END = 'Failed to schedule blackout end',

    HANDOVER_PROCESS_ADD_INPUT = 'Failed to add input',
    HANDOVER_PROCESS_ADD_OUTPUT = 'Failed to add output',

    MATROX_CONFIG_MISMATCH = `Configuration error - unable to set up appliance`,
    MATROX_FETCH_STATE = `Failed to fetch state`,
    MATROX_MISCONFIGURED_GENLOCK = `Misconfigured genlock`,
    MATROX_UPDATE_GENLOCK = `Failed to update genlock`,
    MATROX_MISCONFIGURED_IO_CONFIG = `Misconfigured I/O config`,
    MATROX_UPDATE_IO_CONFIG = `Failed to update I/O config`,
    MATROX_MISCONFIGURED_SDI_PORTS = `Misconfigured SDI ports`,
    MATROX_UPDATE_SDI_PORT = `Failed to update SDI port`,
    MATROX_MISCONFIGURED_PIPELINE = `Misconfigured pipeline`,
    MATROX_ADD_MISSING_PIPELINE = `Failed to add missing pipeline`,
    MATROX_DELETE_OBSOLETE_MPEG2TS_INPUT = `Failed to delete obsolete MPEG2TS input`,
    MATROX_DELETE_OBSOLETE_PROCESSOR = `Failed to delete obsolete processor`,
    MATROX_DELETE_OBSOLETE_ENCODER = `Failed to delete obsolete encoder`,
    MATROX_DELETE_OBSOLETE_MPEG2TS_OUTPUT = `Failed to delete obsolete MPEG2TS output`,
    COMPRIMATO_UNHEALTHY_PIPELINE = 'Unhealthy Comprimato pipeline',
    STORAGE_THRESHOLD_ALERT = 'Storage threshold alert',
    UNHEALTHY_INSTANCES_ALERT = 'Unhealthy instances alert',
    KAFKA_UNAVAILABLE = 'Redpanda unavailable',
    K8S_SYSTEM_ALERT = 'Kubernetes system alert',
    WAL_ARCHIVING_FAILING = 'Config database WAL archiving is failing',

    DOWNLOAD_AD_FILE = 'Failed to download ad file',
    PARSE_VAST_RESPONSE = 'Failed to parse VAST response',
}

/**
 * @OAS_DESCRIPTION Alarm severity levels
 *
 * * critical - Critical alarms are service-affecting meaning several streams are not being delivered correctly.
 *   Critical alarms require immediate attention even if they occur outside working hours. For example if a video-node
 *   is overloaded.
 *
 * * major - Some functionallity is missing or degraded. This could be the control-plane is not working correctly or a
 *   stream cannot be delivered correctly. For example metrics being unavailable.
 *
 * * minor - A fault has occurred but it is currently not service-affecting. For example a database is operating without
 *   adequate redundancy.
 *
 * * warning - Warnings does not affect the service. They indicate potential future problems if not addressed. For
 *   example high disk usage.
 */
export enum AlarmSeverityLevels {
    critical = 'critical',
    major = 'major',
    minor = 'minor',
    warning = 'warning',
}

export enum VaAlarmOwnSeverityLevels {
    cleared = 'cleared',
}

export enum AlarmType {
    'va' = 'va',
    'edge' = 'edge',
    'backend' = 'backend',
    'backupMonitor' = 'backup-monitor',
    'prometheus' = 'prometheus',
}

interface AlarmBase {
    alarmCause: AlarmCause
    alarmSeverity: AlarmSeverityLevels | VaAlarmOwnSeverityLevels
    applianceId?: string
    applianceName?: string
    channelId?: number
    groupId?: string
    inputId?: string
    object: string
    objectName: string
    objectPurpose?: string
    outputId?: string
    physicalPortId?: string
    streamId?: number
    text?: string
    time: string
    type: AlarmType
    clearExplicitly?: boolean
    region?: string
}

/**
 * @OAS_DESCRIPTION An alarm report is used to report alarm events, raising or clearing, to the system.
 */
export interface AlarmReport extends AlarmBase {
    id: string
    active: boolean
}

/**
 * AlarmReports are sent from appliances through the public API/OAS. Backend then translates these reports
 * to InternalAlarmReports before storing them to the database.
 */
export interface InternalAlarmReport extends AlarmBase {
    id: string
    active: boolean
    inputName?: string
    outputName?: string
}

/**
 * @OAS_DESCRIPTION An alarm event that has been registered by the system.
 */
export interface Alarm extends AlarmBase {
    id: bigint
    alarmId: string
    inputName?: string
    outputName?: string
    raisedAt?: string
    clearedAt?: string
    repeatCount: number
}

export type AlarmWithImpact = Alarm & {
    affectedInput?: Input['id']
    affectedOutput?: Output['id']
    affectedAppliance?: Appliance['id']
}

export type AlarmUpdate = {
    status: 'CLEAR'
}

/**
 * @OAS_DESCRIPTION A historical alarm event that has been registered by the system.
 */
export type AlarmLog = Alarm

// Alarm payload for Kafka — derived from Alarm type, with id as string for JSON serialization
export type AlarmKafkaPayload = Omit<Alarm, 'id'> & { id: string }

// Notification topic: raise/clear/update events
export interface AlarmNotificationMessage {
    version: 1
    eventType: 'raise' | 'clear' | 'update'
    alarmId: string
    timestamp: string // ISO 8601
    alarm?: AlarmKafkaPayload // full alarm data on raise
    changes?: Partial<AlarmKafkaPayload> // changed fields on clear/update
}

// Snapshot topic: one message per active alarm
export interface AlarmSnapshotEntry {
    version: 1
    snapshotId: string // UUID shared across batch
    snapshotTimestamp: string // ISO 8601
    alarm: AlarmKafkaPayload
}

export interface BackupMonitorAlarm extends AlarmReport {
    type: AlarmType.backupMonitor
    alarmCause: AlarmCause.BACKUP_JOB_FAILED
}

export enum PrometheusAlerts {
    // These values MUST match the rule names in the prometheus.values file
    LowRedpandaDiskSpace = 'LowRedpandaDiskSpace',
    HighNodeDiskUsage = 'HighNodeDiskUsage',
    ReducedCloudNativePGAvailability = 'ReducedCloudNativePGAvailability',
    CloudNativePGReplicationLag = 'CloudNativePGReplicationLag',
    CloudNativePGReplicationFailure = 'CloudNativePGReplicationFailure',
    ReducedCloudNativePGReplication = 'ReducedCloudNativePGReplication',
    CloudNativePGWALArchiveFailing = 'CloudNativePGWALArchiveFailing',
    CloudNativePGWALVolumeFilling = 'CloudNativePGWALVolumeFilling',
    ReducedRedisAvailability = 'ReducedRedisAvailability',
    KafkaSendErrors = 'KafkaSendErrors',
    ReducedRedpandaAvailability = 'ReducedRedpandaAvailability',
    RedpandaConsumerGroupLag = 'RedpandaConsumerGroupLag',
    ReducedRedpandaToClickHouseAvailability = 'ReducedRedpandaToClickHouseAvailability',
    ReducedClickHouseAvailability = 'ReducedClickHouseAvailability',
    ClickHouseHighLatency = 'ClickHouseHighLatency',
    KubeClientCertificateExpiration = 'KubeClientCertificateExpiration',
}

/**
 * @OAS_DESCRIPTION Parameters on which a list of alarm-log entries can be filtered
 */
export interface AlarmLogFilter extends SearchableFilter {
    fromDate?: Date
    toDate?: Date
    severity?: AlarmSeverityLevels
}

/**
 * @OAS_DESCRIPTION Codec capabilities supported by the appliance
 */
export interface SupportedAudioCodec {
    name: AudioCodec
    bitratesKbps: number[]
    layouts: AudioLayout[]
    qualityOptions?: { name: string; value: string }[]
    bitDepth?: number[]
    disabledForStreamIndices?: number[]
}

export enum MatroxEncoderAACQualityLevel {
    low = 'Level0',
    medium = 'Level1',
    high = 'Level2',
    unknown = 'LevelUnknown',
}

export interface AudioFeatures {
    maxAudioStreams: number
    /**
     * The highest embedded SDI audio pair the appliance can de-embed or embed. Most SDI hardware
     * handles 8 pairs (SMPTE ST 299-1); Edge SDI appliances from R4.1 handle 16 (SMPTE ST 299-2).
     */
    maxAudioPairs?: SdiAudioPair
    /**
     * @OAS_NAME AudioCustomPid
     */
    customPid: { enabled: boolean; required?: boolean }
    codecs: SupportedAudioCodec[]
}

export type VideoFlags = Nimbra400VideoFlags | VideonVideoFlags | MatroxVideoFlags | EdgeSdiVideoFlags

export interface VideoFeatures {
    codecs: SupportedVideoCodec[]
    customPid: { enabled: boolean; required?: boolean }
    latencyModes?: { name: string; value: string }[]
    scalingModes?: { name: string; value: string }[]
    /**
     * @OAS_NAME VideoFeatureFlags
     */
    flags?: VideoFeatureFlags[]
    /**
     * GPU device selector for hardware encoding (e.g., "/dev/dri/renderD128" for Intel GPU)
     */
    gpuSelector?: { name: string; value: string }[]
    bufferSize?: IntegerRangeSpec
    gopSizeFrames?: IntegerRangeSpec
    /**
     * Whether the encoder supports embedding closed captions (A53/CEA-708)
     */
    embedCC?: boolean
    /**
     * Whether the encoder supports SMPTE-2038 ancillary data
     */
    smpte2038?: boolean
    /**
     * Whether the encoder supports configurable ancillary data bitrate
     */
    ancBitrate?: boolean
    /**
     * Whether the encoder supports SDT (Service Description Table) insertion
     */
    sdt?: boolean
}

export interface VideoFeatureFlags {
    name: string
    value: VideoFlags
    disabledForCodecs: VideoCodec[]
    /** Show in advanced encoder settings section */
    advancedSetting?: boolean
    /** Only show in dev mode */
    requiresDevMode?: boolean
    tooltip?: string
}

export interface IntegerRangeSpec {
    min: number
    max: number
}

export type ArrayKeys<T> = Exclude<
    {
        [k in keyof T]: Required<T>[k] extends Array<any> ? k : never
    }[keyof T],
    undefined
>

/**
 * Restrict the range of valid values for a property
 * by declaring a dependency on another property
 * along with a mapping from values of this property
 * to valid values.
 */
export interface Restriction<T> {
    determinedBy: keyof T
    mapping: {
        [value: string]: (string | number)[]
    }
}

export type Restrictions<T> = {
    [property in Exclude<keyof T, 'restrictions'>]?: Restriction<T>
}

export interface Option<Name extends string = string, Value extends string = string> {
    name: Name
    value: Value
}

export type VideoBitDepth = 8 | 10 | 12

export interface SupportedVideoCodec {
    name: VideoCodec | RawVideo
    profile?: { name: string; value: string }[]
    pixelFormat?: { name: string; value: string }[]
    colorSampling?: { name: string; value: string }[]
    bitrate: IntegerRangeSpec

    bitDepth?: VideoBitDepth[]
    resolution?: { name: string; value: string }[]
    scanRate?: VideoFrameRate[]

    scan?: { name: string; value: string }[]
    restrictions?: Restrictions<SupportedVideoCodec>

    scte35Insertion?: Scte35FeatureSupport // Undefined means no support
    gopStructures?: { name: string; value: string }[]
    /**
     * Encoder type: "cpu" for software encoding or "qsv" for Intel Quick Sync Video hardware encoding.
     */
    encoderType?: { name: string; value: string }[]
}

export interface Scte35FeatureSupport {
    customPid: boolean
}

export type VideoFrameRate =
    | 'Follow source'
    | 'Half input'
    | '12'
    | '14.985'
    | '23.976'
    | '24'
    | '25'
    | '29.97'
    | '30'
    | '47.952'
    | '48'
    | '50'
    | '59.94'
    | '60'
    | '100'
    | '119.88'
    | '120'

export interface EncoderFeatures {
    audio: AudioFeatures
    video: VideoFeatures
}

export interface DecoderFeatures {
    audio: AudioFeatures
    video: VideoFeatures
    decoderType?: { name: string; value: string }[]
    delayMs?: IntegerRangeSpec
}

export interface FecLimits {
    maxSize: number
    colRange: IntegerRangeSpec
    rowRange: IntegerRangeSpec
}

interface SupportedInputMode {
    mode: PortMode
    prettyName?: string
    subModes?: string[]
    encoder?: EncoderFeatures
}

interface SupportedOutputMode {
    mode: PortMode
    prettyName?: string
    subModes?: string[]
    decoder?: DecoderFeatures
}

export interface ApplianceFeatures {
    coaxPorts?: number
    input?: {
        modes: SupportedInputMode[]
    }
    output?: {
        modes: SupportedOutputMode[]
    }
    fec?: FecLimits
    proxy?: ApplianceProxySettings
}

export enum ApplianceType {
    nimbraVAdocker = 'nimbraVAdocker',
    nimbra410 = 'nimbra410',
    nimbra412 = 'nimbra412',
    nimbra414 = 'nimbra414',
    nimbra414b = 'nimbra414b',
    nimbraVA225 = 'nimbraVA225',
    nimbraVA220 = 'nimbraVA220',
    edgeConnect = 'edgeConnect',
    core = 'core',
    thumb = 'thumb',
    videon = 'videon',
    matroxMonarchEdgeE4_8Bit = 'matroxMonarchEdgeE4_8Bit',
    matroxMonarchEdgeE4_10Bit = 'matroxMonarchEdgeE4_10Bit',
    matroxMonarchEdgeD4 = 'matroxMonarchEdgeD4',
    matroxMonarchEdgeS1 = 'matroxMonarchEdgeS1',
    comprimato = 'comprimato',
    mediakindRx1 = 'mediakindRx1',
    mediakindCe1 = 'mediakindCe1',
    riversilica = 'riversilica',
    nimbra204 = 'nimbra204',
    edgeSdi = 'edgeSdi',
}

export type ApplianceRoutingRole = 'core' | 'thumb' | 'edge'

export enum ApplianceVariant {
    nimbraVAdocker = 'nimbraVAdocker',
    nimbra410 = 'nimbra410',
    nimbra412 = 'nimbra412',
    nimbra414 = 'nimbra414',
    nimbra414b = 'nimbra414b',
    nimbraVA225 = 'nimbraVA225',
    nimbraVA220 = 'nimbraVA220',
    edgeConnect = 'edgeConnect',
    core = 'core',
    thumb = 'thumb',
    videon = 'videon',
    matroxMonarchEdgeE4_8Bit = 'matroxMonarchEdgeE4_8Bit',
    matroxMonarchEdgeE4_10Bit = 'matroxMonarchEdgeE4_10Bit',
    matroxMonarchEdgeD4 = 'matroxMonarchEdgeD4',
    matroxMonarchEdgeS1 = 'matroxMonarchEdgeS1',
    comprimato = 'comprimato',
    mediakindRx1 = 'mediakindRx1',
    mediakindCe1 = 'mediakindCe1',
    riversilica = 'riversilica',
    nimbra204 = 'nimbra204',
    edgeSdi = 'edgeSdi',
    nimbra410b = 'nimbra410b',
    nimbra520 = 'nimbra520',
}

export type Latitude = number
export type Longitude = number
export type Coordinates = [Latitude, Longitude]
export type CoordinatesMapbox = [Longitude, Latitude]

export interface GeoLocation {
    location: string
    coordinates: Coordinates
}

export interface NdiInfo {
    sources: NdiSource[]
}

export interface NdiSource {
    name: string
}

export interface TcpdumpResponse {
    stdout: string
    stderr: string
    error?: { code: string | number | undefined | null; killed?: boolean | undefined; signal: string | undefined }
    startAt: Date
    stopAt: Date
    timedOut?: boolean
}

export interface Pcap {
    id: string
    name: string
    result?: TcpdumpResponse
    /** Size of the capture file on disk, if available (requires an appliance that reports it). */
    fileSizeBytes?: number
}

export interface InputPcapRequest {
    applianceId: string
    /** Capture duration in seconds, 1-3600. Defaults to 10. */
    durationSeconds?: number
}

export interface InputPcapResponse {
    applianceId: string
    tcpdumpCommand: TcpdumpCommand
    id: string // pcap id (scoped by appliance) available at /api/appliance/:applianceId/pcap/:id
}

export interface OutputPcapRequest {
    applianceId: string
    /** Capture duration in seconds, 1-3600. Defaults to 10. */
    durationSeconds?: number
}

export interface OutputPcapResponse {
    applianceId: string
    tcpdumpCommand: TcpdumpCommand
    id: string // pcap id (scoped by appliance) available at /api/appliance/:applianceId/pcap/:id
}

export interface Overlay {
    id: string
    name: string
    previewResolution: '640x480' | '1280x720' | '1920x1080' | '3840x2160'
    components: OverlayComponent[]
}

export type OverlayInit = Omit<Overlay, 'id'>

export type OverlayComponent = OverlayComponentImage | OverlayComponentText

export interface OverlayCoordinate {
    value: number
    baseline: 'start' | 'end'
}

export interface OverlayComponentImage {
    id: string
    type: 'image'
    data: string
    x: OverlayCoordinate
    y: OverlayCoordinate
    maxHeight: number
    maxWidth: number
    opacity: number
}

// Background and border box
export interface OverlayBox {
    borderWidth: number
    opacity: number
    color: string
}

export interface OverlayComponentText {
    id: string
    type: 'text'
    text: string
    x: OverlayCoordinate
    y: OverlayCoordinate
    fontSize: number
    color: string
    box?: OverlayBox
    preset?: 'timecode' | 'datetime' | 'codec' | 'encoder'
}

export type DeletedOverlay = Entity

interface TransformationProfileInputInfo {
    id: string
    name: string
    synchronized: boolean
    diff?: Record<string, string>
}

export interface TransformationProfile {
    id: string
    name: string
    ingestTransform: IngestTransform
    createdAt: Date
    createdBy: string
    groupId: string
    /**
     * @OAS_NAME TransformationProfileInputInfo
     */
    inputs?: TransformationProfileInputInfo[]
}

export interface TransformationProfileInit {
    name: string
    ingestTransform: IngestTransform
}

export interface TransformationProfileFilter extends SearchableFilter {
    type?: string
}

export type RegionReference = Pick<Region, 'id' | 'name'>
export type ApplianceReference = Pick<BaseAppliance, 'id' | 'name' | 'type' | 'version'>

/**
 * @OAS_DESCRIPTION A single IPv4 kernel route, as reported by `ip -json route show` on the appliance
 */
export interface RouteEntry {
    /** Destination prefix: 'default' or CIDR, e.g. '192.168.1.0/24' */
    destination: string
    /** Next-hop gateway address; absent for on-link (scope 'link') routes */
    gateway?: string
    /** Egress interface name, e.g. 'enp1s0' */
    dev: string
    /** Protocol that installed the route, e.g. 'dhcp', 'kernel', 'static' */
    proto?: string
    /** Route scope, e.g. 'global', 'link', 'host' */
    scope?: string
    /** Preferred source address for this route */
    src?: string
    /** Route priority; lower wins */
    metric?: number
    /** True when the kernel flags the route's link as down */
    linkDown?: boolean
}

export interface BaseAppliance {
    id: string
    type: ApplianceType
    role: ApplianceRoutingRole
    variant: ApplianceVariant
    name: string
    hostname: string
    serial: string
    contact: string
    version: ApplianceVersion
    owner: Group['id']
    physicalPorts: PhysicalPort[]
    alarms: Alarm[]
    metrics?: {
        cpu: {
            usage: number // 0 - 1
        }
        memory: {
            usage: number // 0 - 1
        }
    }
    features: ApplianceFeatures
    logLevel: LogLevel
    ristserverLogLevel: RistserverLogLevel
    collectHostMetrics: boolean
    unhealthyAlarm?: 'off' // enabled by default
    cordon?: boolean
    lastMessageAt?: Date
    lastRegisteredAt?: Date
    health?: ApplianceStatus
    region: RegionReference
    secondaryRegion?: RegionReference
    geoLocation?: GeoLocation
    settings: CommonApplianceSettings
    ndiSources?: NdiSource[]
    tags?: MetadataTag['name'][]
    transcodeAccelerator?: Transcoder
    platformMetadata?: PlatformMetadata
    /** IPv4 kernel routing table, continuously reported by edge-control/va-control */
    routeTable?: RouteEntry[]
}

interface NimbraVAAppliance extends BaseAppliance {
    settings: NimbraVAApplianceSettings
}

export interface NimbraVADockerAppliance extends NimbraVAAppliance {
    type: ApplianceType.nimbraVAdocker
}

export interface Nimbra410Appliance extends NimbraVAAppliance {
    type: ApplianceType.nimbra410
}

export interface Nimbra412Appliance extends NimbraVAAppliance {
    type: ApplianceType.nimbra412
}

export interface Nimbra414Appliance extends NimbraVAAppliance {
    type: ApplianceType.nimbra414
}

export interface Nimbra414bAppliance extends NimbraVAAppliance {
    type: ApplianceType.nimbra414b
}

export interface NimbraVA220Appliance extends NimbraVAAppliance {
    type: ApplianceType.nimbraVA220
}

export interface NimbraVA225Appliance extends NimbraVAAppliance {
    type: ApplianceType.nimbraVA225
}

export interface EdgeConnectAppliance extends BaseAppliance {
    type: ApplianceType.edgeConnect
}

export interface CoreAppliance extends BaseAppliance {
    type: ApplianceType.core
}

export interface ThumbAppliance extends BaseAppliance {
    type: ApplianceType.thumb
}

export interface VideonAppliance extends BaseAppliance {
    type: ApplianceType.videon
}

export interface ComprimatoAppliance extends BaseAppliance {
    type: ApplianceType.comprimato
}

export interface RiversilicaAppliance extends BaseAppliance {
    type: ApplianceType.riversilica
}

export interface MediakindRx1Appliance extends BaseAppliance {
    type: ApplianceType.mediakindRx1
}

export interface MediakindCe1Appliance extends BaseAppliance {
    type: ApplianceType.mediakindCe1
}

export interface Nimbra204Appliance extends BaseAppliance {
    type: ApplianceType.nimbra204
}

export interface EdgeSdiAppliance extends BaseAppliance {
    type: ApplianceType.edgeSdi
    settings: EdgeSdiApplianceSettings
}

export interface MatroxE4Appliance extends BaseAppliance {
    /**
     * @OAS_NAME MatroxE4ApplianceType
     */
    type: ApplianceType.matroxMonarchEdgeE4_8Bit | ApplianceType.matroxMonarchEdgeE4_10Bit
    settings: MatroxE4ApplianceSettings
}

export interface MatroxD4Appliance extends BaseAppliance {
    type: ApplianceType.matroxMonarchEdgeD4
    settings: MatroxD4ApplianceSettings
}

export interface MatroxS1Appliance extends BaseAppliance {
    type: ApplianceType.matroxMonarchEdgeS1
    settings: MatroxS1ApplianceSettings
}

/**
 * @OAS_DESCRIPTION An appliance, e.g. EdgeConnect, is a piece of software that runs on a physical or virtual machine (multiple appliances can run on the same machine).
 * An appliance registers itself with the Edge backend.
 * After registration it is possible to create inputs and outputs on the ports (network interfaces) on the appliance.
 */
export type Appliance =
    | CoreAppliance
    | EdgeConnectAppliance
    | NimbraVADockerAppliance
    | Nimbra410Appliance
    | Nimbra412Appliance
    | Nimbra414Appliance
    | Nimbra414bAppliance
    | NimbraVA220Appliance
    | NimbraVA225Appliance
    | MatroxE4Appliance
    | MatroxD4Appliance
    | MatroxS1Appliance
    | ThumbAppliance
    | VideonAppliance
    | ComprimatoAppliance
    | MediakindRx1Appliance
    | MediakindCe1Appliance
    | RiversilicaAppliance
    | Nimbra204Appliance
    | EdgeSdiAppliance

export interface ApplianceInputInfo {
    applianceId: string
    applianceName: string
    inputId: string
    inputName: string
    inputGroup: string
    inputAdminStatus: InputAdminStatus
}

export interface ApplianceOutputInfo {
    applianceId: string
    applianceName: string
    inputId?: string
    inputName?: string
    inputAdminStatus?: InputAdminStatus
    outputId: string
    outputName: string
    outputGroup: string
    outputAdminStatus: OutputAdminStatus
}

export type ApplianceSummary = Pick<Appliance, 'id' | 'name' | 'hostname' | 'type' | 'tags' | 'platformMetadata'> & {
    isActive: boolean
}

export interface DrainApplianceResult {
    appliance: Appliance
    inputsRerouted: number
    inputsFailed: number
}

export interface MigrateApplianceSerialArguments {
    serial: string
}

export interface TunnelOutputInfo {
    outputId: string
    outputName: string
    outputAdminStatus: OutputAdminStatus
    inputId?: string

    // The last downstream tunnel from the requested tunnel.
    finalTunnelId: number
}

export interface ApplianceProxySettings {
    // Setting enabled to true/false overrides the default proxy enabled setting based on the appliance type.
    enabled?: boolean
    // Setting this will override the whitelist of allowed cookies to forward to the appliance from the client when using the UI proxy.
    forwardedCookies?: string[]
    forwardedHeaders?: string[]
}

export interface CommonApplianceSettings {
    useDynamicTunnelClientSourceAddress?: boolean
    useEphemeralTunnelSenderPorts?: boolean
    proxy?: ApplianceProxySettings

    capabilities?: ApplianceCapabilities
}

export interface MatroxDecoderGenlock {
    source: MatroxDecoderGenlockSource
    framerateFamily: MatroxDecoderFramerateFamily
}

export interface NimbraVAApplianceSettings extends CommonApplianceSettings {
    autoRouteTunnels?: boolean
}

export interface MatroxE4ApplianceSettings extends CommonApplianceSettings {
    /**
     * @OAS_NAME MatroxE4ApplianceGenlockSettings
     * @OAS_DESCRIPTION Applicable for encoder
     */
    genlock: {
        isEnabled: boolean
        resolution: MatroxEncoderGenlockResolution
    }

    // Matrox v2. undefined means 'v1.4' as older versions do not report apiVersion.
    apiVersion?: 'v1.4' | 'v2'

    // Matrox v2. Indices of input SDI ports with PCM mode enabled, index 0-3.
    sdiPortsWithPcmModeEnabled?: number[]

    /**
     * @OAS_NAME MatroxE410BitApplianceDecoderGenlockSettings
     * @OAS_DESCRIPTION Only applicable for the 10-bit E4 appliance with repurposed SDI output/decoder ports (I/O configuration).
     */
    decoderGenlock?: MatroxDecoderGenlock // Matrox v2. Only applicable for the 10-bit E4.

    // Matrox v2. Only applicable for the 10-bit E4 (i.e. not 8-bit). Defaults to 4:0
    ioConfig?: MatroxEncoderIOConfig
}

export enum MatroxEncoderIOConfig {
    fourZero = '4:0',
    threeOne = '3:1',
    twoTwo = '2:2',
}

export enum MatroxEncoderGenlockResolution {
    ntsc = 'NTSC',
    pal = 'PAL',
}

export interface MatroxD4ApplianceSettings extends CommonApplianceSettings {
    /**
     * @OAS_NAME MatroxD4ApplianceGenlockSettings
     */
    genlock: MatroxDecoderGenlock

    // Matrox v2. undefined means 'v1.4' as older versions do not report apiVersion.
    apiVersion?: 'v1.4' | 'v2'
}

export interface MatroxS1ApplianceSettings extends CommonApplianceSettings {
    /**
     * @OAS_NAME MatroxS1ApplianceGenlockSettings
     */
    genlock: MatroxDecoderGenlock

    // Matrox v2. undefined means 'v1.4' as older versions do not report apiVersion.
    apiVersion?: 'v1.4' | 'v2'

    // Matrox v2. Indices of input SDI ports with PCM mode enabled
    sdiPortsWithPcmModeEnabled?: number[]
}

export enum MatroxDecoderGenlockSource {
    internal = 'internal',
    external = 'external',
}

/**
 * Genlock source for EdgeSdi appliances with a Deltacast SDI board.
 * Dektec boards do not support a configurable genlock source.
 */
export enum SdiGenlockSource {
    local = 'local',
    externalReference = 'externalReference',
}

/**
 * Video standard for the SDI board's local genlock oscillator.
 * Required when genlockSource is "local"; not allowed otherwise.
 */
export enum SdiLocalVideoStandard {
    sd480i59_94 = '480i59.94',
    sd576i50 = '576i50',
    hd720p50 = '720p50',
    hd1080i59_94 = '1080i59.94',
    hd1080p50 = '1080p50',
    hd1080p59_94 = '1080p59.94',
    uhd2160p50 = '2160p50',
    uhd2160p59_94 = '2160p59.94',
}

export interface EdgeSdiApplianceSettings extends CommonApplianceSettings {
    // Genlock source for the SDI board.
    genlockSource?: SdiGenlockSource
    // Video standard for the local genlock oscillator. Used only when genlockSource is "local".
    localVideoStandard?: SdiLocalVideoStandard
}

export enum MatroxDecoderFramerateFamily {
    familyOne = '25/50',
    familyTwo = '29.97/59.94',
    familyThree = '30/60',
}

export type ApplianceSettings = Appliance['settings']

export interface ApplianceStatus {
    title: string
    state: ApplianceConnectionState
}

export enum ApplianceConnectionState {
    connected = 'connected',
    missing = 'missing',
    neverConnected = 'neverConnected',
}

export interface ApplianceVersion {
    vaVersion?: string // Deprecated - will use firmwareVersion in future
    controlImageVersion?: string
    controlSoftwareVersion?: string
    dataImageVersion?: string
    dataSoftwareVersion?: string
    ristServerVersion?: string
    storVersion?: string
    firmwareVersion?: string
    requestedVersion?: string
}

/**
 * @OAS_DESCRIPTION Parameters on which a list of groups can be filtered.
 */
export interface GroupFilter extends SearchableFilter {
    id?: string
    ids?: string[]
    name?: string
    excludeIds?: string[]

    /// Return the groups with access to ANY (i.e. not all) of the received input ids
    withAccessToAnyInputs?: string[]

    /// Return the groups with no access to any one of the received input ids
    withoutAccessToAnyInputs?: string[]
}

export enum PortType {
    'ip' = 'ip',
    'coax' = 'coax',
    'videon' = 'videon',
    'ndi' = 'ndi',
}

export interface PhysicalPortListItem extends PhysicalPort {
    logicalPorts: Array<{ id: string; direction: 'input' | 'output' }>
}

export interface Address {
    /**
     * @DATA_FORMAT IPV4_OR_EMPTY_STRING
     */
    interRegionPublicAddress?: string

    /**
     * @DATA_FORMAT IPV4_OR_EMPTY_STRING
     */
    publicAddress?: string

    /**
     * @DATA_FORMAT IPV4_OR_EMPTY_STRING
     */
    internalAddress?: string

    /**
     * @DATA_FORMAT IPV4_OR_EMPTY_STRING
     */
    address: string
    netmask?: string
}

/**
 * Per-port configuration exposed for SDI/ASI (coax) ports.
 * Populated from the registration-time CoaxPort.configuration.
 */
export interface CoaxPortConfigurationInfo {
    vendor?: 'deltacast' | 'dektec'
    boardId?: number
    boardType?: number
}

/**
 * @OAS_DESCRIPTION A PhysicalPort represents a NIC (network interface card) on an appliance.
 */
export interface PhysicalPort extends Entity {
    addresses: Array<Address>
    appliance: ApplianceReference
    // Only used for addressing SDI ports (even though ethernet ports are also given an index)
    index?: string
    // Empty string for SDI ports
    mac: string
    inputs?: Array<Input['id']>
    name: string
    outputs?: Array<Output['id']>
    owner: Group['id']
    portType: PortType
    networks: Array<Network>
    // If the port can only be used in a specific direction
    direction?: PortDirection
    // Set on coax (SDI/ASI) ports only — identifies the SDI hardware behind this port.
    coaxConfiguration?: CoaxPortConfigurationInfo
    // when true the interface is visible/usable only by the system group; hidden from all other groups for inputs/outputs.
    restrictedToSystemGroup?: boolean
}

export type PhysicalPortInfo = Omit<PhysicalPort, 'appliance'> & { occupiedPorts: OccupiedPort[]; isDisabled: boolean }

export interface AppliancePhysicalPortInfo {
    appliance: ApplianceReference & Pick<Appliance, 'settings' | 'features' | 'variant'>
    physicalPorts: PhysicalPortInfo[]
}

export interface SharedPort {
    port: PhysicalPort['id']
    owner: Group['id']
    networks?: Array<Network['id']>
    // See PhysicalPort.restrictedToSystemGroup.
    restrictedToSystemGroup?: boolean
}

export interface IngestTransformBase {
    type: string
    profile?: TransformationProfile['id']
}

export enum PIDRuleAction {
    Map = 'map',
    Delete = 'delete',
    SetNull = 'setNull',
}

export type PIDRule = PIDRuleMap | PIDRuleDelete | PIDRuleSetNull

export interface PIDRuleMap {
    action: PIDRuleAction.Map
    pid: number
    destPid: number
}

export interface PIDRuleDelete {
    action: PIDRuleAction.Delete
    pid: number
}

export interface PIDRuleSetNull {
    action: PIDRuleAction.SetNull
    pid: number
}

/**
 * @OAS_EXCLUDED
 *
 * This field is excluded because the functionallity is not yet entierly ready. The version of mpts_demux bundled with
 * this version has issues supporting multiple outputs with pidmans as the pidmaps gets mingled between outputs.
 */
export interface PIDMap {
    rules: PIDRule[]
}

export enum TransformationPlacementStrategy {
    InputAppliance = 'input-appliance',
    IngressCore = 'ingress-core',
    Fixed = 'fixed',
}

export type InputAppliancePlacement = { strategy: TransformationPlacementStrategy.InputAppliance }
export type IngressCorePlacement = { strategy: TransformationPlacementStrategy.IngressCore }
export type FixedPlacement = { strategy: TransformationPlacementStrategy.Fixed; appliances: string[] }

/**
 * @OAS_DESCRIPTION Specifies where a transformation should take place
 */
export type TransformationPlacement = InputAppliancePlacement | IngressCorePlacement | FixedPlacement

// TODO: Rename? this is not a 'mtps' demux when it can support SPTS
export interface MptsDemuxTransform extends IngestTransformBase {
    type: 'mpts-demux'
    services: number[]
    pidMap?: PIDMap
}

export type TranscodePixelFormat = 'yuv420p' | 'yuv422p' | 'yuv444p' | 'yuv420p10le' | 'yuv422p10le' | 'yuv444p10le'
export type TranscodeAudioCodec = 'aac' | 'mp2' | 'ac3'
export type TranscodeAacProfile = 'LC' | 'HE' | 'HEv2'
export type TranscodeAacQuality = 'low' | 'medium' | 'high'

export const FFMPEG_AUDIO_CODEC_BITRATE_KBPS: Record<TranscodeAudioCodec, number[]> = {
    // Source: https://github.com/FFmpeg/FFmpeg/blob/33e6d57/libavcodec/mpegaudiotabs.h#L29
    // ff_mpa_bitrate_tab[0][0] = MPEG1 Layer I bitrates
    // ff_mpa_bitrate_tab[0][1] = MPEG1 Layer II bitrates (we are interested in this)
    // ff_mpa_bitrate_tab[0][2] = MPEG1 Layer III bitrates
    mp2: [32, 48, 56, 64, 80, 96, 112, 128, 160, 192, 224, 256, 320, 384],

    // ac3: Dolby Digital AC-3 - NO enforcement
    ac3: [],

    // aac: Advanced Audio Codec - NO enforcement
    aac: [],
}

export interface StrictTranscodeParams {
    pixelFormat: TranscodePixelFormat
    codec: 'h262' | 'h264' | 'h265'
    bitrate: number
    bitrateMode: 'CBR' | 'VBR'
    videoBitrate: number
    framerate: '25' | '30000/1001' | '50' | '60000/1001'
    scan: 'progressive' | 'top' | 'bottom'
    resolution: '3840:2160' | '1920:1080' | '1280:720' | '720:576' | '640:480'
    audioCodec: TranscodeAudioCodec
    audioBitrate: number
    audioSampleRate: number
    audioAacProfile?: TranscodeAacProfile
}

export type TranscodeParams = Partial<StrictTranscodeParams>

export interface TranscodeTransformationParams extends Partial<TranscodeParams> {
    type: 'transcode'
    overlayId?: string
    deinterlace?: 'yadif'
    audioAacQuality?: TranscodeAacQuality
    forceSoftwareEncoding?: boolean
    gpuSelector?: GpuSelector
}

export type FramerateConversionFramerate = '25' | '30000/1001' | '50' | '60000/1001'
export type FramerateConversionCodec = 'mpeg2' | 'h264' | 'h265'
export type FramerateConversionPixelFormat = '420_8' | '420_10' | '422_8' | '422_10'

export interface TranscodeTransform extends IngestTransformBase {
    type: 'transcode'
    ffmpegParams: TranscodeTransformationParams
}

export interface FramerateConversionTransform extends IngestTransformBase {
    type: 'framerate-conversion'
    framerate: FramerateConversionFramerate
    /** @OAS_NAME FrameformerQualityTier */
    qualityTier?: FrameformerQualityTier
    /** Enables the demo overlay and bills usage as the FrameFormer demo service type. */
    demoMode?: boolean
    /** Output video codec for the encoded transport stream. Defaults to H.264 if not set. */
    codec?: FramerateConversionCodec
    /** Output video pixel format for the encoded transport stream. Defaults to 4:2:0 8-bit if not set. */
    pixelFormat?: FramerateConversionPixelFormat
    /** Output video bitrate in bits per second. Defaults to 10 Mbps if not set. */
    bitrate?: number
    /** Output video PID for the encoded transport stream. Defaults to 256 if not set. */
    pid?: number
    /** Max threads for motion-compensated interpolation. Defaults to available CPU count. */
    maxThreads?: number
    /** Motion compensation effort percentage (0-100). Defaults to 50. 0 disables motion compensation. */
    effort?: number
    /** Starting CPU core index for the framerate conversion workload. Defaults to 2. */
    cpuCoreIndex?: number
    /** Realtime scheduling priority (1-99). Defaults to 80. */
    realtimePrio?: number
}

export interface AudioReshufflingStream {
    pid: number
    channels: number
}

export interface AudioReshufflingPassthrough {
    inputPid: number
    outputPid: number
}

export interface AudioReshufflingChannel {
    pid: number
    channel: number
}

export interface AudioReshufflingMapping {
    input: AudioReshufflingChannel
    output: AudioReshufflingChannel
}

export interface AudioReshufflingParams {
    inputs: AudioReshufflingStream[]
    outputs: AudioReshufflingStream[]
    mappings: AudioReshufflingMapping[]
    passthrough: AudioReshufflingPassthrough[]
}

export interface AudioReshufflingTransformationParams {
    type: 'audio-reshuffling'
    audioReshuffling: AudioReshufflingParams
    audioCodec: TranscodeAudioCodec
    audioBitrate?: number
    audioAacProfile?: TranscodeAacProfile
    audioAacQuality?: TranscodeAacQuality
}

export interface AudioReshufflingTransform extends IngestTransformBase {
    type: 'audio-reshuffling'
    ffmpegParams: AudioReshufflingTransformationParams
}

export interface AdInsertionConfigBase {
    adServer?: string
    adUrl?: string
    adPod?: string
    defaultAdPod?: string
    blackoutAdPod?: string
}

export type AdInsertionDefaultConfig = AdInsertionConfigBase

export interface AdInsertionProgramConfig extends AdInsertionConfigBase {
    programNumber: number
}

export interface AdInsertionParams {
    scteEventIdFilter?: number[]
    sctePidFilter?: number[]
    enableBlackout?: boolean
    fileReadaheadMs?: number
    defaultConfig: AdInsertionDefaultConfig
    programs?: AdInsertionProgramConfig[]
    /** Output bitrate in bits per second for the mux transform */
    bitrate?: number
}

export interface AdInsertionTransform extends IngestTransformBase {
    type: 'ad-insertion'
    adInsertion: AdInsertionParams
}

/**
 * PID remapping rule condition - specifies which PIDs to match
 * - number: Match a specific PID [32..8191)
 * - 'any_video_stream': Match any PID declared as video stream in PMT
 * - 'any_audio_stream': Match any PID declared as audio stream in PMT
 */
export type PidRemappingRuleCondition = number | 'any_video_stream' | 'any_audio_stream'

/**
 * PID remapping rule action - specifies what to do with matched PIDs
 * - number: Remap to a specific PID [32..8191)
 * - 'drop': Drop all packets matching the rule
 * - {from, to}: Remap PID automatically to a given range
 */
export type PidRemappingRuleAction = number | 'drop' | { from: number; to: number }

/**
 * @OAS_DESCRIPTION A rule that specifies how to remap or drop specific PIDs
 */
export interface PidRemappingRule {
    /** Condition to match PIDs (specific PID number, or special values like 'any_video_stream') */
    if: PidRemappingRuleCondition
    /** Action to take on matched PIDs (remap to specific PID, 'drop', or range mapping) */
    remap: PidRemappingRuleAction
}

/**
 * @OAS_DESCRIPTION Parameters for PID remapping transformation
 */
export interface PidRemappingParams {
    /** If defined, list of program numbers to keep (others are dropped). Program 0 is always preserved. */
    programsAllowList?: number[]
    /** Ordered list of rules for PIDs. Rules are evaluated in order. */
    pidsRules?: PidRemappingRule[]
    /** Output bitrate in bits per second for the mux transform */
    bitrate?: number
}

/**
 * @OAS_DESCRIPTION PID remapping transformation - allows filtering programs and remapping/dropping PIDs within a ristserver stream
 */
export interface PidRemappingTransform extends IngestTransformBase {
    type: 'pid-remapping'
    pidRemapping: PidRemappingParams
}

/**
 * @OAS_NAME IngestTransform
 */
export type IngestTransform =
    | MptsDemuxTransform
    | TranscodeTransform
    | AudioReshufflingTransform
    | AdInsertionTransform
    | PidRemappingTransform
    | FramerateConversionTransform

export function isFramerateConversionTransform(
    ingestTransform?: IngestTransform
): ingestTransform is FramerateConversionTransform {
    return ingestTransform?.type === 'framerate-conversion'
}

/**
 * @OAS_DESCRIPTION Contains data used to create a new Input.
 */
export interface InputInit {
    name: string
    adminStatus: InputAdminStatus
    bufferSize?: number
    ports?: InputPort[]
    maxBitrate?: number | null
    thumbnailMode: ThumbnailMode
    videoPreviewMode?: VideoPreviewMode
    // Non-present/omitted value is interpreted as "true" for backward compatibility.
    tr101290Enabled?: boolean
    broadcastStandard?: BroadcastStandard
    handoverMethod?: HandoverMethod

    /**
     * @OAS_NAME DerivableInputSource
     */
    deriveFrom?: {
        parentInput: string
        delay: number
        ingestTransform?: IngestTransform
        transformationPlacement?: TransformationPlacement
    }

    unhealthyAlarm?: AlarmSeverityLevels | keyof typeof AlarmSeverityLevels | null // default is null

    tags?: MetadataTag['name'][]
}

/**
 * @OAS_DESCRIPTION Contains data used to update an existing Input.
 */
export type InputUpdate = Pick<
    Input,
    | 'name'
    | 'tr101290Enabled'
    | 'broadcastStandard'
    | 'handoverMethod'
    | 'adminStatus'
    | 'videoPreviewMode'
    | 'ports'
    | 'thumbnailMode'
    | 'maxBitrate'
    | 'bufferSize'
    | 'deriveFrom'
    | 'unhealthyAlarm'
    | 'tags'
>

export type InputAdminStatusUpdate = Pick<Input, 'id' | 'adminStatus'>
export type OutputAdminStatusUpdate = Pick<Output, 'id' | 'adminStatus'>

/**
 * @OAS_DESCRIPTION Contains data used to switch input and routing mode of an existing Output.
 */
export interface OutputSwitchInput {
    input: Output['input'] | null
    routing?: Output['routing']
}

export enum InputAdminStatus {
    off = 0,
    on = 1,
}

export enum ThumbnailMode {
    none = 0,
    edge = 1,
    core = 2,
}

/* This is just for debugging, we should probably remove it*/
export type InputStreamGraphDebug = {
    id: string
    graph: string
}

/**
 * @OAS_DESCRIPTION Parameters on which a list of alarms can be filtered.
 */
export interface AlarmFilter extends SearchableFilter {
    inputIds?: string[]
    includeCleared?: boolean
    applianceId?: string
}

/**
 * @OAS_DESCRIPTION Parameters on which a list of inputs can be filtered.
 */
export interface InputFilter extends SearchableFilter {
    id?: string
    ids?: string[]
    inputName?: string
    group?: string
    parentId?: string
    applianceId?: string
    applianceIds?: string[]
    physicalPortIds?: PhysicalPort['id'][]
    canSubscribe?: boolean
    tr101290Window?: MetricWindow
    derived?: boolean
    adminStatus?: InputAdminStatus
    healthStatuses?: InputOperStatus[]

    inputApplianceNames?: string[]
    inputApplianceRegions?: string[]
    ownerGroupName?: string
    tags?: MetadataTag['name'][]
    /** @OAS_NAME InputFilterTransformationTypes */
    transformationTypes?: IngestTransform['type'][]

    channelGroups?: number[]

    /**
     * @OAS_DESCRIPTION Performance optimisation. Set this property to true to allow the backend to omit input metrics, health and tsInfo.
     */
    omitMetrics?: boolean
}

export interface OccupiedPort {
    physicalPortId: string
    inputId?: string
    outputId?: string
    tunnelId?: number

    logicalPortId: string
    portMode?: PortMode | 'tunnel-client' | 'tunnel-server'
    localPortNumbers: number[]
}

export enum VideoPreviewMode {
    off = 'off',
    ondemand = 'on demand',
    alwayson = 'always on',
}

export enum HandoverMethod {
    udp = 'udp',
    unix = 'unix',
    internal = 'internal',
}

/**
 * @OAS_DESCRIPTION Represents a stream (or multiple streams for redundancy) ingested into the Edge cluster.
 * An Input can be assigned to one or multiple Outputs for distribution.
 */
export interface Input extends InputInit, Entity {
    channelGroup: number
    // The channelId property will be removed in the future. Use channelIds instead.
    channelId: number
    channelIds: number[]
    canSubscribe: boolean
    previewUrl?: string // hls preview url
    owner?: string
    alarms?: AlarmWithImpact[]
    metrics?: InputMetrics
    tsInfo?: TransportStream[]
    health?: InputStatus
    numOutputs?: number
    numSharedGroups?: number

    /// Source/ingress appliances only
    appliances?: LimitedAppliance[]

    // This is the ingress core codes for inputs on edge appliances,
    // meaning that for core inputs it will be empty.
    downstreamAppliances: DownstreamAppliance[] // Sorted: [primary, secondary]
    createdAt: Date
    updatedAt: Date
    /// true if this input is misconfigured, e.g. 'localIp' does not exist on the associated appliance
    misconfigured?: boolean
    previewSettings?: {
        mode: VideoPreviewMode
    }

    /**
     * @OAS_EXCLUDED true if this input has redundant streams (ingress > 1)
     */
    isRedundant?: boolean

    derivedInputs?: LimitedInput[]
}

export interface LimitedInput extends Pick<Input, 'id' | 'name'> {}

export interface DeletedInput {
    id: Input['id']
    name: Input['name']
}

/**
 * @OAS_DESCRIPTION enum OutputRedundancyMode: [none = 0 | failover = 1 | active = 2]
 */
export enum OutputRedundancyMode {
    none = 0,
    failover = 1,
    active = 2,
}

export type OutputVideoCodec = VideoCodec | RawVideo

type PartialBy<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>
// Currently only applicable for Comprimato which has output transcoding capabilities
export type OutputEncoderSettings = PartialBy<GeneralEncoderSettings<OutputVideoCodec>, 'totalBitrate'>

/**
 * @OAS_DESCRIPTION Contains data used to create a new Output.
 */
export interface OutputInit {
    adminStatus: OutputAdminStatus
    // The preferred redundancy mode - will be applied when possible; actual redundancy may differ.
    redundancyMode?: OutputRedundancyMode
    group?: string
    object?: 'output'
    /**
     * @DATA_FORMAT UUID
     */
    input?: string
    // Defaults to automatic
    routing?: { type: 'automatic' } | { type: 'p2p'; tunnelIds: Array<Tunnel['id']> }
    // delay in milliseconds
    delay?: number
    delayMode?: DelayMode
    name: string
    ports: OutputPort[]
    unhealthyAlarm?: AlarmSeverityLevels | keyof typeof AlarmSeverityLevels | null
    tags?: MetadataTag['name'][]
}

/**
 * @OAS_DESCRIPTION Output enabled/disabled status.
 * * Disabled: 0
 * * Enabled: 1
 */
export enum OutputAdminStatus {
    off = 0,
    on = 1,
}

/**
 * @OAS_DESCRIPTION Represents a stream egressed by the associated appliance and sent to a specified destination.
 * An Output can receive data from one Input.
 */
export interface Output extends Entity, OutputInit {
    createdAt: Date
    updatedAt: Date
    group: string
    alarms?: AlarmWithImpact[]
    metrics?: OutputMetrics
    tsInfo?: TransportStream[]
    health?: OutputStatus
    channelIds?: number[]
    appliances: LimitedAppliance[]
    /// Includes the source appliance
    upstreamAppliances: LimitedAppliance[]
    /// true if this output is misconfigured, e.g. 'localIp' does not exist on the associated appliance
    misconfigured?: boolean
}

/**
 * @OAS_DESCRIPTION Contains data used to update an existing Output.
 */
export type OutputUpdate = Pick<
    Output,
    | 'name'
    | 'input'
    | 'ports'
    | 'delay'
    | 'delayMode'
    | 'adminStatus'
    | 'redundancyMode'
    | 'unhealthyAlarm'
    | 'tags'
    | 'routing'
>

/**
 * @OAS_DESCRIPTION Parameters on which a list of outputs can be filtered.
 */
export interface OutputFilter extends SearchableFilter {
    id?: string
    ids?: string[]
    // input id
    input?: string
    inputName?: string
    // group id
    group?: string
    groups?: string[]
    groupName?: string
    // appliance id
    appliance?: string
    // appliance ids
    appliances?: string[]
    applianceNames?: string[]
    physicalPortIds?: PhysicalPort['id'][]
    adminStatus?: OutputAdminStatus
    healthStatuses?: OutputOperStatus[]

    /// hasInput: If true: only fetch outputs that have an input. If false: only fetch outputs that have no inputs.
    hasInput?: boolean
    /// belongingToInputIds: If populated: only fetch outputs belonging to the specified input ids
    belongingToInputIds?: string[]
    /// notBelongingToInputIds: If populated: only fetch outputs NOT belonging to the specified input ids
    notBelongingToInputIds?: string[]

    /// The desired tr101290-metric window to fetch
    inputTr101290Window?: MetricWindow

    regionNames?: string[]
    tags?: MetadataTag['name'][]

    /**
     * @OAS_DESCRIPTION Performance optimisation. Set this property to true to allow the backend to omit output metrics, health and tsInfo.
     */
    omitMetrics?: boolean
}

export interface SearchableFilter {
    searchName?: string
}

/**
 * @OAS_DESCRIPTION Parameters on which the appliance summary list of appliances can be filtered.
 */
export interface ApplianceSummaryFilter {
    tags?: MetadataTag['name'][]
    types?: ApplianceType[]
}

/**
 * @OAS_DESCRIPTION Parameters on which a list of appliances can be filtered.
 */
export interface ApplianceFilter extends SearchableFilter {
    id?: string
    ids?: string[]
    core?: boolean
    type?: ApplianceType
    types?: ApplianceType[]
    roles?: ApplianceRoutingRole[]
    variant?: ApplianceVariant
    variants?: ApplianceVariant[]
    group?: string
    groupName?: string
    serials?: string[]
    serial?: string
    region?: Region['id']
    regionName?: Region['name']
    secondaryRegionName?: Region['name']
    regions?: Region['id'][]
    regionNames?: Region['name'][]
    tags?: MetadataTag['name'][]
    omitMetrics?: boolean
}

/**
 * @OAS_DESCRIPTION Parameters on which a list of address mappings can be filtered.
 */
export interface IpMappingFilter extends SearchableFilter {
    privateIp?: string
    region?: string
}

/**
 * @OAS_DESCRIPTION Parameters on which a list of tunnels can be filtered.
 */
export interface TunnelFilter {
    ids?: string[]
    client?: string
    clientName?: string
    clientType?: ApplianceType
    clients?: string[]
    appliance?: string
    appliances?: string[]
    applianceNames?: string[]
    server?: string
    serverName?: string
    serverType?: ApplianceType
    servers?: string[]
    type?: TunnelType
    input?: string
    // Group must have access at least one of client or server physical ports
    physicalPortGroup?: Group['id']
    physicalPortIds?: PhysicalPort['id'][]
}

/**
 * @OAS_DESCRIPTION Parameters on which a list of ports can be filtered.
 */
export interface PortFilter extends SearchableFilter {
    id?: string
    core?: boolean
    appliance?: string
    applianceType?: ApplianceType
    owner?: Group['id']
    portType?: PortType
    network?: Network['id']
}

/**
 * @OAS_DESCRIPTION Parameters on which a list of output recipient lists can be filtered.
 */
export interface OutputListFilter extends SearchableFilter {
    id?: string
    ids?: string[]
    name?: string
    group?: string
}

/**
 * @OAS_DESCRIPTION Parameters on which a list of group recipient lists can be filtered.
 */
export interface GroupRecipientListFilter extends SearchableFilter {
    id?: string
    name?: string
    group?: string
    withoutAccessToAnyInputs?: string[]
}

const SDI_AUDIO_PAIRS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16] as const
export const sdiAudioPairs = [...SDI_AUDIO_PAIRS] // get the not ReadOnly array
export type SdiAudioPair = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15 | 16

/**
 * Pairs available without SMPTE ST 299-2 support. Appliances declare their actual limit via
 * {@link AudioFeatures.maxAudioPairs}; absent means this legacy limit.
 */
export const DEFAULT_MAX_SDI_AUDIO_PAIRS: SdiAudioPair = 8

export type SdiAudioChannelSide = 'l' | 'r'

export interface SdiAudioChannel {
    pair: SdiAudioPair
    side: SdiAudioChannelSide
}

export const sdiAudioChannels: SdiAudioChannel[] = sdiAudioPairs.flatMap((pair) =>
    (['l', 'r'] as const).map((side) => ({ pair, side }))
)

/** Channel specs in embedded order: '1l','1r','2l',…,'16r'. */
export const sdiAudioChannelSpecs: string[] = sdiAudioChannels.map(({ pair, side }) => `${pair}${side}`)

export enum AudioLayout {
    mono = 'mono',
    stereo = 'stereo',
}

/** The audio channel positions an SDI layout can populate */
export type SdiAudioChannelLayoutKey = 'frontLeft' | 'frontRight' | 'frontCenter'

/**
 * The channel positions each layout has. Array order is a contract: the first entry is the primary
 * channel the others are derived from (and the one `pair` is taken from). `Record<AudioLayout, …>`
 * forces an entry when a new layout is added to the enum.
 */
export const AUDIO_LAYOUT_CHANNELS: Record<AudioLayout, SdiAudioChannelLayoutKey[]> = {
    [AudioLayout.stereo]: ['frontLeft', 'frontRight'],
    [AudioLayout.mono]: ['frontCenter'],
}

export enum AudioCodec {
    mpeg2 = 'mpeg-2',
    aacLc = 'aac-lc',
    heAac = 'he-aac',
    aac = 'aac', // AAC family, used for VA decoder only since VA doesn’t distinguish between aac-lc and he-aac audio streams in the decoder
    heAacV2 = 'he-aac v2',
    aes3 = 'aes3',
    ac3pt = 'ac3pt',
    ac3 = 'ac3',
    aacLatm = 'aac latm',
    aacAdts = 'aac adts',
    raw = 'raw',
}

export enum AudioSystemStandard {
    systemA = 'System A',
    systemB = 'System B',
}

export type AudioStream = GenericAudioStream<Exclude<AudioCodec, AudioCodec.ac3pt>> | Ac3AudioStream

/** Display labels for AudioLanguageAudioType; the Record forces a label when ristserver gains a value. */
export const AUDIO_LANGUAGE_AUDIO_TYPE_LABEL: Record<AudioLanguageAudioType, string> = {
    undefined: 'Undefined',
    cleanEffects: 'Clean effects',
    hearingImpaired: 'Hearing impaired',
    visualImpairedCommentary: 'Visual impaired commentary',
    primary: 'Primary',
    native: 'Native',
    emergency: 'Emergency',
    primaryCommentary: 'Primary commentary',
    alternateCommentary: 'Alternate commentary',
}

/**
 * ISO 639 language signalling for an audio stream, emitted as a PMT language descriptor.
 * Only honored by Edge SDI encoder inputs (ristserver).
 */
export interface AudioStreamLanguage {
    /** ISO 639-2 alpha-3 code in lowercase, e.g. "eng", "swe", "spa". */
    code: string
    /**
     * @OAS_NAME AudioStreamLanguageAudioType
     */
    audioType?: AudioLanguageAudioType
}

/**
 * @OAS_NAME GenericAudioStream
 */
export interface GenericAudioStream<Codec extends AudioCodec> {
    type: AudioLayout
    /**
     * @OAS_NAME GenericAudioStreamCodec
     */
    codec: Codec
    quality?: string
    pair: SdiAudioPair
    bitrate: number
    bitDepth?: number
    pid?: number
    /**
     * Per-channel SDI source mapping — a `<pair><side>` spec (e.g. "3l") per layout channel.
     * Authoritative for Edge SDI; other SDI appliances use `pair` instead.
     */
    channelLayout?: {
        frontLeft?: string
        frontRight?: string
        frontCenter?: string
    }
    encoderOptions?: Record<string, string>
    language?: AudioStreamLanguage
}

export interface Ac3AudioStream extends GenericAudioStream<AudioCodec.ac3pt> {
    codec: AudioCodec.ac3pt
    systemStandard: AudioSystemStandard
}

export const supportedDecodeAudioCodecs = [AudioCodec.aes3, AudioCodec.mpeg2, AudioCodec.aac, AudioCodec.ac3pt] as const

type DecodeAudioCodec = (typeof supportedDecodeAudioCodecs)[number]

export interface DecodeAudioStream {
    type: AudioLayout
    /**
     * @OAS_NAME DecodeAudioCodec
     */
    codec?: DecodeAudioCodec
    pair: SdiAudioPair
    pid?: number
    /** See {@link GenericAudioStream.channelLayout}. */
    channelLayout?: {
        frontLeft?: string
        frontRight?: string
        frontCenter?: string
    }
}

export interface AncSettings {
    forward: boolean
    supervise: boolean
}

export interface Smpte2038Configuration {
    '1': AncSettings
    '2': AncSettings
    '3': AncSettings
    '4': AncSettings
    '5': AncSettings
    '6': AncSettings
    '7': AncSettings
    '8': AncSettings
    '9': AncSettings
    '10': AncSettings
    '11': AncSettings
    '12': AncSettings
    '13': AncSettings
    '14': AncSettings
    '15': AncSettings
    '16': AncSettings
    '17': AncSettings
    '18': AncSettings
    '19': AncSettings
    '20': AncSettings
    '21': AncSettings
    '22': AncSettings
    '23': AncSettings
}

export enum VideoCodec {
    mpeg2 = 'mpeg2',
    h264 = 'h.264',
    h265 = 'h.265',
}

export type RawVideo = 'RAW'
export const RawVideo: RawVideo = 'RAW'

export enum MatroxEncoderScalingOption {
    // Unscaled from top left
    noScalingAtTopLeft = 'noScalingAtTopLeft',
    // Unscaled centered
    noScalingAtCenter = 'noScalingAtCenter',
    // Stretched to all edges
    scaleToFitDestination = 'scaleToFitDestination',
    // Scaled to all edges
    scaleToFillButKeepContentAspectRatio = 'scaleToFillButKeepContentAspectRatio',
    // Scaling to the nearest edge
    scaleToFitAllContent = 'scaleToFitAllContent',
}

export enum MatroxEncoderPivotOption {
    none = 'none',
    by90Degrees = 'by90Degrees',
    by180Degrees = 'by180Degrees',
    by270Degrees = 'by270Degrees',
}

export enum MatroxEncoderFlipOption {
    none = 'none',
    horizontally = 'horizontally',
    vertically = 'vertically',
    bothAxis = 'bothAxis',
}

export enum MatroxEncoderPixelFormat {
    YUV420_8bits = 'YUV420_8bits',
    YUV422_8bits = 'YUV422_8bits',
    YUV422_10bits = 'YUV422_10bits',
    YUV420_10bits = 'YUV420_10bits',
}

export enum MatroxEncoderVideoProfile {
    Baseline = 'Baseline',
    Main = 'Main',
    High = 'High',
    High10Bit = 'High 10-bit',
    High422 = 'High YUV 4:2:2',
}

export enum MatroxEncoderProcessorFrameRate {
    twentyFive = 25,
    twentyNineNinetySeven = 29.97,
    thirty = 30,
    fifty = 50,
    fiftyNineNinetyFour = 59.94,
    sixty = 60,
}

export interface MatroxEncoderProcessorSettings {
    audioSourceSettings: MatroxEncoderProcessorAudioSourceSettings

    // Continue streaming on signal loss?
    inputFailSafe: boolean

    // Follow the input video source and apply the same video settings.
    // Can only be 'true' if there is a single video source (i.e. if layout is 'single') and enableTestMode is disabled.
    // Setting it to 'false' will apply Progressive encoding,
    // but then you need to configure the scaling options on the video input source, which is currently not possible via the API.
    followVideoSourceInput: boolean
    videoLayout: 'single' | 'quad'
    videoSourceSettings: MatroxEncoderProcessorVideoSourceSettings[] // One entry for each layout/video source

    /**
     * @OAS_NAME MatroxEncoderProcessorEnforcedCanvasSettings
     * @OAS_DESCRIPTION Settings applicable when not following the input video source settings.
     */
    overriddenCanvasSettings: {
        frameSize: {
            // Must be between 64 - 4096 and evenly divisible by 16
            width: number
            // Must be between 64 - 4096 and evenly divisible by 2
            height: number
        }
        frameRate: MatroxEncoderProcessorFrameRate

        // Useful if you e.g. don't stretch the video, i.e. the background is visible behind the video.
        backgroundColor: { red: number; green: number; blue: number; alpha: number }

        pixelFormat: MatroxEncoderPixelFormat
    }

    // Matrox API V2:
    enableTestMode?: boolean
}

export interface MatroxEncoderProcessorAudioSourceSettings {
    // TODO: Add support for non-sdi "Analog audio input".
    // The port index of the SDI port to receive from
    portIndex: number
}

export interface MatroxEncoderProcessorVideoSourceSettings {
    // The port index of the SDI port to receive from
    portIndex: number

    // 1 == Use all frames. Defaults to 1.
    frameCaptureRate: number

    scaling: MatroxEncoderScalingOption

    // default 0 degrees
    pivot: MatroxEncoderPivotOption

    // default 'none'
    flip: MatroxEncoderFlipOption

    // 0 - 100, default 100
    opacityPercentage: number

    // 0 - 1000, default 500
    brightness: number

    // 0 - 1000, default 500
    contrast: number

    // 0 - 360, default 0
    hue: number

    // 0 - 1000, default 500
    saturation: number
}

export enum MatroxEncoderIncludeOption {
    none = 'none',
    audioOnly = 'audioOnly',
    videoOnly = 'videoOnly',
    audioAndVideo = 'audioAndVideo',
}

export enum MatroxEncoderEncodingMode {
    lowLatency = 'lowLatency',
    highQuality = 'highQuality',
}

export enum MatroxEncoderBitrateControlMode {
    constant = 'constant',
    variable = 'variable',
}

export enum MatroxEncoderAACEncoder {
    lc = 'lc',
    hev1 = 'hev1',
    hev2 = 'hev2',
}

export enum MatroxEncoderAACFormat {
    noContainer = 'noContainer',
    adts = 'adts',
    adif = 'adif',
    unknown = 'unknown',
}

export interface MatroxEncoderSettings {
    include: MatroxEncoderIncludeOption

    /**
     * @OAS_NAME MatroxEncoderVideoSettings
     */
    videoSettings: {
        forceEncodingSize: boolean

        /**
         * @OAS_NAME MatroxEncoderEnforcedVideoSettings
         * @OAS_DESCRIPTION Settings applied when 'forceEncodingSize' is true
         */
        forcedEncodingSettings: {
            frameSize: {
                // Must be between 64 - 4096 and evenly divisible by 16
                width: number
                // Must be between 64 - 4096 and evenly divisible by 16
                height: number
            }
            scaling: MatroxEncoderScalingOption
            pivot: MatroxEncoderPivotOption
            flip: MatroxEncoderFlipOption
        }

        forcePixelFormat: boolean
        // Applied when 'forcePixelFormat' is true
        forcedPixelFormat: MatroxEncoderPixelFormat

        // Must be compatible with the 'enforcedPixelFormat'.
        encodingProfile: MatroxEncoderVideoProfile

        // Must be between 0.05 - 120 Mb/s
        targetBitrateMbps: number

        // Not visible in Matrox UI
        useLosslessEncoding: false

        /**
         * @OAS_NAME MatroxEncoderVideoGopSettings
         */
        gop: {
            // Must be greater than the pFrameInterval
            iFrameInterval: number
            // Insert a P-frame every <pFrameInterval> frame
            pFrameInterval: number
            // Not in matrox ui
            temporalIframeInterval: 0
        }

        /**
         * @OAS_NAME MatroxEncoderVideoRateControlSettings
         */
        rateControl: {
            mode: MatroxEncoderEncodingMode
            // Must be between 0-51
            quantizationMin: number
            // Must be between 0-51
            quantizationMax: number
            bitrateControl: MatroxEncoderBitrateControlMode
            // Not in matrox ui
            allowFrameSkipping: false
            // Must be between 0.1 - 120 Mb/s, and greater than or equal to the target bitrate
            maxBitrateMbps: number
        }

        forceCAVLCEntropyEncoding: boolean
    }

    /**
     * @OAS_NAME MatroxEncoderAudioSettings
     */
    audioSettings: {
        aacEncoder: MatroxEncoderAACEncoder
        // lc: 32 - 576 Kb/s
        // hev1: 32-288 Kb/s
        // hev2: 32-144 Kb/s
        bitrateKbps: number
        aacQuality: MatroxEncoderAACQualityLevel
        useTemporalNoiseShaping: boolean
        aacFormat: MatroxEncoderAACFormat

        // Available in MatroxV2
        // Only applicable if receiving from an SDI port with PCM mode enabled.
        numAudioChannels?: 2 | 4 | 6 | 8 | 16
    }
}

export interface MatroxEncoderConfig {
    type: 'matroxEncoder'
    processorSettings: MatroxEncoderProcessorSettings
    encoderSettings: MatroxEncoderSettings
    tsOutputSettings: MatroxEncoderTSOutputSettings
}

export interface MatroxEncoderTSOutputSettings {
    isAncEnabled: boolean
    // Only relevant if sdiPort.pcmMode is disabled (pcmMode available in Matrox V2)
    selectedAudioPairs: MatroxAudioChannelPair[]

    // Matrox v2.
    enable104To35?: boolean
}

export interface GeneralEncoderSettings<TVideoCodec extends string = VideoCodec> {
    videoCodec: TVideoCodec
    /**
     * Total TS bitrate on Nimbra 400 and Edge SDI
     */
    totalBitrate: number
    /**
     * Edge SDI input UX: video encoder target bitrate
     */
    videoBitrate?: number
    /**
     * Edge SDI input UX: selected encoding profile
     */
    encodingMode?: EdgeSdiEncodingMode
    /**
     * Edge SDI input UX: video safety factor above the itemized mux model
     */
    muxFactor?: number
    gopSizeFrames: number
    audioStreams: AudioStream[]
    enableScte35?: boolean
    scte35Pid?: number
    embedCC?: boolean
    enableSmpte2038?: boolean
    smpte2038?: Smpte2038Configuration
    smpte2038Pid?: number

    latencyMode?: string
    scalingMode?: string
    gopStructure?: string
    videoFlags?: Partial<Record<VideoFlags, boolean>>
    profile?: string
    pixelFormat?: string

    colorSampling?: string

    videoPid?: number
    bitDepth?: number
    resolution?: string
    scanRate?: string

    scan?: string

    // For ristserver SDI inputs
    gpuSelector?: GpuSelector
    bufferSize?: number | null
    maxBFrames?: number
    closedGOP?: boolean
    ancBitrate?: number
    encoderType?: 'cpu' | 'qsv'

    encoderOptions?: Record<string, string>

    // SDT (Service Description Table) configuration
    enableSdt?: boolean
    sdtServiceName?: string
    sdtServiceProvider?: string
}

export type EncoderSettings = GeneralEncoderSettings | MatroxEncoderConfig

export enum Nimbra400VideoFlags {
    smpte12mTimecode = 'smpte12mTimecode',
}

export enum VideonVideoFlags {
    limitTo30Fps = 'limitTo30Fps',
    klvTimeCodeInsertion = 'klvTimeCodeInsertion',
    scte35Insertion = 'scte35Insertion',
    cea708_608Captions = 'cea708_608Captions',
    smpte2038Insertion = 'smpte2038Insertion',
}

export enum MatroxVideoFlags {
    ForceCAVLCEntropyEncoding = 'ForceCAVLCEntropyEncoding',
}

export enum EdgeSdiVideoFlags {
    intraRefresh = 'intraRefresh',
    smpte2038 = 'smpte2038',
}

export enum EdgeSdiEncodingMode {
    ultraLowLatency = 'ultraLowLatency',
    lowLatency = 'lowLatency',
    bestQuality = 'bestQuality',
}

export enum GpuSelector {
    internalGpu = 'internalGpu',
    discreteGpu = 'discreteGpu',
}

export type PortMode =
    | IpPortMode
    | CoaxPortMode
    | IpcPortMode
    | VideonPortMode
    | MatroxPortMode
    | ComprimatoPortMode
    | InternalDerivedInputPortMode

/**
 * @OAS_DESCRIPTION Supported transport protocols over IPC.
 */
export enum IpcPortMode {
    unix = 'unix', // Unix sockets
}

/**
 * @OAS_DESCRIPTION Derived input port mode.
 */
export enum InternalDerivedInputPortMode {
    'internal' = 'internal',
}

/**
 * @OAS_DESCRIPTION Supported transport protocols over IP.
 */
export enum IpPortMode {
    'rist' = 'rist',
    'udp' = 'udp',
    'rtp' = 'rtp',
    'srt' = 'srt',
    'zixi' = 'zixi',
    'rtmp' = 'rtmp',
    'generator' = 'generator',
}

/**
 * @OAS_DESCRIPTION Supported video ports
 */
export enum CoaxPortMode {
    // 'hdmi' = 'hdmi',
    'sdi' = 'sdi',
    'asi' = 'asi',
}

export enum MatroxPortMode {
    'matroxSdi' = 'matroxSdi',
}

export enum VideonPortMode {
    'videonSdi' = 'videonSdi',
    'videonHdmi' = 'videonHdmi',
    'videonAuto' = 'videonAuto',
}

export enum ComprimatoPortMode {
    'comprimatoSdi' = 'comprimatoSdi',
    'comprimatoNdi' = 'comprimatoNdi',
}

export enum Output3gLevel {
    'A' = 'A',
    'B' = 'B',
}

export enum OutputPortFec {
    '1D' = '1D',
    '2D' = '2D',
}

export interface IpPortAddress {
    /**
     * @DATA_FORMAT IPV4_OR_EMPTY_STRING
     */
    address: string
    /**
     * @DATA_FORMAT PORT
     */
    port: number
}

export interface IpPortListenAddress extends IpPortAddress {
    /**
     * @DATA_FORMAT PORT_LISTENING
     */
    port: number

    /**
     * @DATA_FORMAT IPV4_CIDR_BLOCK
     */
    whitelistCidrBlock?: string | string[]
}

export interface IpPortSourceAddress {
    /**
     * @DATA_FORMAT IPV4_OR_EMPTY_STRING
     */
    sourceAddress?: string
    localPort?: number
}

export interface IpMulticastAddress {
    /**
     * @DATA_FORMAT IPV4_OR_EMPTY_STRING
     */
    multicastAddress?: string

    /**
     * @DATA_FORMAT IPV4_OR_EMPTY_STRING
     */
    multicastSource?: string
}

export interface InternalDerivedInputSinkPort extends OutputPortBase {
    mode: InternalDerivedInputPortMode.internal
}

export interface InternalDerivedInputSourcePort extends PortBase, IngestTransformMixin {
    mode: InternalDerivedInputPortMode.internal
}

export interface UnixOutputPort extends OutputPortBase {
    mode: IpcPortMode.unix
    remotePath: string
}

export interface UnixInputPort extends PortBase, IngestTransformMixin {
    mode: IpcPortMode.unix
    localPath: string
}

export type IpcInputPort = UnixInputPort

export interface UdpOutputPortCommon
    extends OutputPortBase, IpPortAddress, IpMulticastAddress, IpPortSourceAddress, RegionalPort {
    ttl?: number
}

export interface UdpInputPortCommon extends PortBase, IpPortListenAddress, IpMulticastAddress, RegionalPort {}

export interface RistSimpleProfileInputPort extends PortBase, IpPortAddress, RegionalPort {
    mode: IpPortMode.rist
    profile: RistProfile.simple
    /**
     * @DATA_FORMAT PORT_LISTENING_EVEN
     */
    port: number

    /**
     * @DATA_FORMAT IPV4_CIDR_BLOCK
     */
    whitelistCidrBlock?: string | string[]
}

export interface RistSimpleProfileOutputPort extends OutputPortBase, IpPortAddress, IpPortSourceAddress, RegionalPort {
    mode: IpPortMode.rist
    profile: RistProfile.simple
    /**
     * @DATA_FORMAT PORT_EVEN
     */
    port: number
}

export interface UdpInputPort extends UdpInputPortCommon, IngestTransformMixin {
    mode: IpPortMode.udp
}

export type GeneratorTimestampResolution = 'seconds' | 'milliseconds'
export type GeneratorResolutionPreset =
    | '1280x720'
    | '1920x1080'
    | '720p' // legacy resolution
    | '1080p' // legacy resolution
/**
 * @OAS_EXCLUDED
 *
 * This field is excluded from the API because sometimes ffmpeg as it is running in edge-data does not start when
 * generating an interlaced stream. Remove this exclution once interlaced video generation works well
 */
export type GeneratorScanMode =
    | 'progressive'
    | 'interlaced_tff' // top frame first
    | 'interlaced_bff' // bottom frame first
export type GeneratorFrameRate = '30' | '60' | '25' | '50' | '29.97' | '59.94'
export type GeneratorCodec = 'h264' | 'h262'

/**
 * @OAS_DESCRIPTION Variable bitrate – VBR. The bitrate will be determined only by what is encoded and can change over time.
 */
export interface GeneratorBitrateVBR {
    type: 'vbr'
}

/**
 * @OAS_DESCRIPTION Constant bitrate – CBR. The generator will try to keep a constant bitrate. The bitrate is in bits/s.
 */
export interface GeneratorBitrateCBR {
    type: 'cbr'
    /**
     * @DATA_FORMAT POSITIVE_INTEGER
     */
    bitrate: number // bitrate in bits/s
}

/**
 * @OAS_DESCRIPTION Encoding bitrate of the stream. Either VBR or CBR.
 */
export type GeneratorBitrate = GeneratorBitrateCBR | GeneratorBitrateVBR

export interface GeneratorInputPort extends PortBase {
    mode: IpPortMode.generator
    audioOnly?: boolean
    timestampResolution?: GeneratorTimestampResolution
    resolution?: GeneratorResolutionPreset
    scanMode?: GeneratorScanMode
    frameRate?: GeneratorFrameRate
    codec?: GeneratorCodec
    /**
     * @DATA_FORMAT PORT_LISTENING
     */
    port?: number
    /**
     * @DATA_FORMAT IPV4_OR_EMPTY_STRING
     */
    address?: string

    bitrate?: GeneratorBitrate

    programs?: Array<{
        title: string
        audio: {
            pid: number
        }
        video: {
            pid: number
        }
    }>
}

export interface UdpOutputPort extends UdpOutputPortCommon {
    mode: IpPortMode.udp
}

export interface RtpInputPort extends UdpInputPortCommon {
    mode: IpPortMode.rtp
    fec?: boolean

    // TODO: EDGE-4263: Make configurable when support for RTP-RTCP is added (currently only available for RIST).
    /**
     * @OAS_EXCLUDED
     */
    rtcp?: boolean

    // Used to distinguish priority between non-binary-equal RTP streams.
    // If omitted the streams must be binary-equal and will be used for SMPTE 2022-7 redundancy
    /** @deprecated. Provide the same channel priority (port.priority) for all ports receiving binary equal streams to enable SMPTE 2022-7 merging */
    /**
     * @OAS_DESCRIPTION
     * @deprecated.
     * Provide the same channel priority (port.priority) for all ports receiving binary equal streams to enable SMPTE 2022-7 merging
     */
    failoverPriority?: number
}

export interface RtmpInputPort extends PortBase, IpPortListenAddress, RegionalPort {
    mode: IpPortMode.rtmp
}

export interface RtmpOutputPort extends OutputPortBase, RegionalPort {
    mode: IpPortMode.rtmp
    rtmpDestinationAddress: string
}

export interface RtpOutputPort extends UdpOutputPortCommon {
    mode: IpPortMode.rtp
    fec?: OutputPortFec
    fecRows?: number
    fecCols?: number
}

export interface SrtPortBase extends OutputPortBase, RegionalPort {
    mode: IpPortMode.srt
    rtp?: boolean // RTP over SRT
    /**
     * @DATA_FORMAT SRT_PASSPHRASE
     */
    passphrase?: string // Used when pbkeylen is not 'none'
    latency: number
    ipttl?: number
    mss?: number
    /** @OAS_EXCLUDED */
    experimentalRistSrt?: true
}

export interface SrtInputPortBase extends SrtPortBase {
    reducedBitrateDetection: boolean
    reducedBitrateThreshold?: number // Used when reducedBitrateDetection is true
    unrecoveredPacketsDetection: boolean
    unrecoveredPacketsThreshold?: number // Used when unrecoveredPacketsDetection is true
}

export interface SrtCallerPort {
    srtMode: SrtMode.caller

    // Only applicable for E-C appliances (i.e. not VA)
    streamId?: string

    // IPV4_OR_HOSTNAME
    remoteIp: string
    /**
     * @DATA_FORMAT PORT
     */
    remotePort: number
    /**
     * @DATA_FORMAT PORT
     */
    localPort?: number // Used to enforce the local outgoing port. Incoming traffic will also be received on this local port.
    /**
     * @DATA_FORMAT IPV4_OR_EMPTY_STRING
     * @OAS_DESCRIPTION
     * Local address to bind the outgoing connection to (one of the selected interface's addresses).
     * Omit or leave empty to let the operating system pick the source address and egress interface.
     */
    localIp?: string
}

export interface SrtListenerPort {
    srtMode: SrtMode.listener

    /**
     * @DATA_FORMAT IPV4_OR_EMPTY_STRING
     */
    localIp: string
    /**
     * @DATA_FORMAT PORT_LISTENING
     */
    localPort: number

    /**
     * @DATA_FORMAT IPV4_CIDR_BLOCK
     */
    whitelistCidrBlock?: string | string[]
}

export interface SrtRendezvousPort {
    srtMode: SrtMode.rendezvous
    /**
     * @DATA_FORMAT IPV4_OR_EMPTY_STRING
     */
    localIp: string

    // IPV4_OR_HOSTNAME
    remoteIp: string
    /**
     * @DATA_FORMAT PORT_LISTENING
     */
    remotePort: number // Specifies both local and remote port. Note that the local port is this way both listening port and outgoing port.

    /**
     * @DATA_FORMAT IPV4_CIDR_BLOCK
     */
    whitelistCidrBlock?: string | string[]
}

export interface SrtBondingPort {
    // `failoverPriority` is used to determine if multiple srt ports on the same appliance should be setup
    // with bonding or use failover in the ristserver.
    // Unset failoverPriority means bonding disabled, i.e. separate srt-live-transmit processes and separate channels in the ristserver.
    // Srt outputs with multiple interfaces are only allowed when failoverPriority is set (bonding is enabled).
    // Equal failoverPriority means srt broadcast bonding.
    // Different failoverPriorities means srt main/backup bonding.
    failoverPriority?: number
}

export interface SrtCallerInputPort extends SrtCallerPort, SrtInputPortBase, SrtBondingPort {}

export interface SrtListenerInputPort extends SrtListenerPort, SrtInputPortBase, SrtBondingPort {}

export interface SrtRendezvousInputPort extends SrtRendezvousPort, SrtInputPortBase {}

export interface SrtCallerOutputPort extends SrtCallerPort, SrtOutputPortBase, SrtBondingPort {}

export interface SrtListenerOutputPort extends SrtListenerPort, SrtOutputPortBase, SrtBondingPort {}

export interface SrtRendezvousOutputPort extends SrtRendezvousPort, SrtOutputPortBase {}

export type SrtInputPort = SrtCallerInputPort | SrtListenerInputPort | SrtRendezvousInputPort
export type SrtOutputPort = SrtCallerOutputPort | SrtListenerOutputPort | SrtRendezvousOutputPort

export interface SrtOutputPortBase extends SrtPortBase {
    pbkeylen: SrtKeylen
    rateLimiting: SrtRateLimiting
    maxBw?: number // Used when rateLimiting is absolute
    inputBw?: number // Used when rateLimiting is not relativeToInput
    oheadBw?: number // Used when rate limiting is relativeToInput
}

export interface ZixiPortBase extends OutputPortBase, RegionalPort {
    mode: IpPortMode.zixi
    zixiMode: ZixiMode
    streamId: string
    password?: string
    unrecoveredPacketsDetection: boolean
    unrecoveredPacketsThreshold?: number // Used when unrecoveredPacketsDetection is true
}

export interface ZixiInputPortBase extends ZixiPortBase {
    decryptKey?: string
    decryptType: ZixiDecryptType
    reducedBitrateDetection: boolean // Only applicable for VA appliances
    reducedBitrateThreshold?: number // Used when reducedBitrateDetection is true
}

export enum ZixiDecryptType {
    none = 'none',
    aes128 = 'aes128',
    aes192 = 'aes192',
    aes256 = 'aes256',
}

export enum ZixiLinkMode {
    single = 'single',
    standby = 'standby',
    bonded = 'bonded',
}

export interface ZixiLink {
    rateLimit?: number

    /**
     * @DATA_FORMAT IPV4_OR_EMPTY_STRING
     */
    localIp: string

    /**
     * @DATA_FORMAT IPV4_OR_EMPTY_STRING
     */
    remoteIp: string
    /**
     * @DATA_FORMAT PORT
     */
    remotePort: number
}

export type ZixiLinkSet = [ZixiLink] | [ZixiLink, ZixiLink] | [ZixiLink, ZixiLink, ZixiLink]

/// Mandatory settings for VA
interface ZixiFecSettings {
    fecLatency?: number
    optimizeFec?: boolean
    adaptiveFec?: boolean
    maxFecOverhead?: number
}

export interface ZixiPullInputPort extends ZixiInputPortBase, ZixiFecSettings {
    zixiMode: ZixiMode.pull
    retransmitBuf: number
    // IPV4_OR_HOSTNAME
    remotePrimaryIp: string
    // IPV4_OR_HOSTNAME
    remoteSecondaryIp?: string

    /**
     * @DATA_FORMAT IPV4_OR_EMPTY_STRING
     */
    localIp?: string

    /**
     * @DATA_FORMAT PORT
     */
    pullPort: number
}

export interface ZixiPushInputPort extends ZixiInputPortBase {
    zixiMode: ZixiMode.push
}

export interface ZixiPullOutputPort extends ZixiPortBase {
    zixiMode: ZixiMode.pull
}

export interface ZixiPushOutputPort extends ZixiPortBase, ZixiFecSettings {
    zixiMode: ZixiMode.push
    linkMode: ZixiLinkMode
    linkSet1: ZixiLinkSet
    linkSet2?: ZixiLinkSet
    retransmitBuf: number
    // maxBitrateMbps is only applicable for zixi-push outputs, which can currently only be set up on core appliances
    maxBitrateMbps?: number
}

export type ZixiInputPort = ZixiPullInputPort | ZixiPushInputPort
export type ZixiOutputPort = ZixiPullOutputPort | ZixiPushOutputPort

export type ApplianceAllocationPurpose = 'input' | 'output'

export interface AllocateApplianceInRegionRequest {
    regionId: string
    purpose?: ApplianceAllocationPurpose // The purpose affects the balancing decisions
    // If populated: will allocate the same appliance as the input is located on, if possible.
    // Should be populated if allocating an appliance for an output.
    inputId?: string
    // If populated: will not allocate any of the excluded appliances.
    excludedApplianceIds?: string[]
}

export interface AllocateSpecificApplianceRequest {
    applianceId: string
    purpose?: ApplianceAllocationPurpose
    // Will not re-use any existing allocations with the received ids, i.e. use to create a new allocation.
    excludedAllocationIds?: string[]
}

export type AllocateApplianceRequest = AllocateApplianceInRegionRequest | AllocateSpecificApplianceRequest

export interface CoreVideoApplianceAllocation {
    id: string
    createdAt: Date
    expiresAt: Date
    appliance: ApplianceReference & { region: string }
}

/**
 * @OAS_DESCRIPTION A subset of a PhysicalPort.
 */
export interface LimitedPhysicalPort {
    id: string
    name: string
    addresses: Address[]
    appliance: ApplianceReference
    portType: PortType
}

/**
 * @OAS_DESCRIPTION
 * The priority of the channel received by the input port.
 * Integer between 0 and 3, where 0 is the highest priority and 3 is the lowest priority.
 * Only applicable for input ports.
 * Provide the same priority on all ports receiving binary equal streams to enable SMPTE 2022-7 merging.
 * Provide a separate priority on ports receiving a unique stream/channel to enable channel group redundancy (i.e., failover based on priority).
 */
export type ChannelPriority = number

export interface PortBase {
    id?: string
    mode: string
    /**
     * @DATA_FORMAT UUID
     */
    physicalPort: string
    copies?: number

    /**
     * @OAS_DESCRIPTION
     * The priority of the channel received by the input port.
     * Integer between 0 and 3, where 0 is the highest priority and 3 is the lowest priority.
     * Only applicable for input ports.
     * Provide the same priority on all ports receiving binary equal streams to enable SMPTE 2022-7 merging.
     * Provide a separate priority on ports receiving a unique stream/channel to enable channel group redundancy (i.e., failover based on priority).
     */
    priority?: ChannelPriority

    /**
     * @OAS_EXCLUDED
     */
    internalStreamId?: number

    /**
     * @OAS_EXCLUDED
     */
    appliance?: string // Read only property used for determining health of multi appliance inputs

    /**
     * @OAS_EXCLUDED
     */
    index?: number // Internal property that indicates the index in the ports array for sorting, it is not returned from the API
}

export interface OutputPortBase extends PortBase {}

export interface RegionalPort extends PortBase {
    // applianceAllocationId is only present when creating/updating a regional input/output (not when fetching)
    applianceAllocationId?: string

    /**
     * @OAS_NAME AllocatedApplianceRegion
     */
    region?: {
        id: string
        name: string
        // Present when fetching a regional port
        allocatedAppliance?: ApplianceReference
    }
}

export interface SdiInputPort extends PortBase {
    mode: CoaxPortMode.sdi
    encoderSettings: GeneralEncoderSettings
}

export interface GeneralDecoderSettings {
    decoderType?: 'cpu' | 'qsv'
    delayMs?: number
    gpuSelector?: GpuSelector
    audioStreams?: DecodeAudioStream[]
}

export interface MatroxSdiInputPort extends PortBase {
    mode: MatroxPortMode.matroxSdi
    encoderSettings: MatroxEncoderConfig
}

export interface VideonInputPortBase extends PortBase {
    encoderSettings: GeneralEncoderSettings
}

export interface VideonSdiInputPort extends VideonInputPortBase {
    mode: VideonPortMode.videonSdi
}

export interface VideonHdmiInputPort extends VideonInputPortBase {
    mode: VideonPortMode.videonHdmi
}

export interface VideonAutoInputPort extends VideonInputPortBase {
    mode: VideonPortMode.videonAuto
}

export interface ComprimatoSdiInput extends PortBase {
    mode: ComprimatoPortMode.comprimatoSdi
    encoderSettings: GeneralEncoderSettings
}

export interface ComprimatoNdiInput extends PortBase {
    mode: ComprimatoPortMode.comprimatoNdi
    encoderSettings: GeneralEncoderSettings
    name: string
}

export interface ComprimatoNdiOutput extends OutputPortBase {
    mode: ComprimatoPortMode.comprimatoNdi
    encoderSettings: OutputEncoderSettings
    name: string
}

export interface ComprimatoSdiOutput extends OutputPortBase {
    mode: ComprimatoPortMode.comprimatoSdi
    encoderSettings: OutputEncoderSettings
}

export interface SdiOutputPort extends OutputPortBase {
    mode: CoaxPortMode.sdi
    output3gLevel?: Output3gLevel
    multiStreamAlignment?: boolean
    genlock?: boolean
    decoderSettings?: GeneralDecoderSettings
    videoPrefillFrames?: number
    autoAdjustAlignerLatency?: boolean
    autoAdjustAlignerCeiling?: number
}

export enum MatroxAudioChannelPair {
    channel1And2 = 0b0000_0000_0000_0011,
    channel3And4 = 0b0000_0000_0000_1100,
    channel5And6 = 0b0000_0000_0011_0000,
    channel7And8 = 0b0000_0000_1100_0000,
    channel9And10 = 0b0000_0011_0000_0000,
    channel11And12 = 0b0000_1100_0000_0000,
    channel13And14 = 0b0011_0000_0000_0000,
    channel15And16 = 0b1100_0000_0000_0000,
}

// Applicable when device "Genlock Framrate family" is set to "25/50"
export enum MatroxDecoderOutputResolutionFamilyOne {
    '720p50' = '720p50',
    '1080p25' = '1080p25',
    '1080p50' = '1080p50',
    '1080i25' = '1080i25',
    '2160p50' = '2160p50', // 4K. Only available on the SDI port 4 (index 3). Will disable the other ports.
}

// Applicable when device "Genlock Framrate family" is set to "29.97/59.94"
export enum MatroxDecoderOutputResolutionFamilyTwo {
    '720p59.94' = '720p59.94',
    '1080p23.98' = '1080p23.98',
    '1080p29.97' = '1080p29.97',
    '1080p59.94' = '1080p59.94',
    '1080i29.97' = '1080i29.97',
    '2160p59.94' = '2160p59.94', // 4K. Only available on the SDI port 4 (index 3). Will disable the other ports.
}

// Applicable when device "Genlock Framrate family" is set to "30/60"
export enum MatroxDecoderOutputResolutionFamilyThree {
    '720p60' = '720p60',
    '1080p24' = '1080p24',
    '1080p30' = '1080p30',
    '1080p60' = '1080p60',
    '1080i30' = '1080i30',
    '2160p60' = '2160p60', // 4K. Only available on the SDI port 4 (index 3). Will disable the other ports.
}

export type MatroxDecoderOutputResolution =
    | MatroxDecoderOutputResolutionFamilyOne
    | MatroxDecoderOutputResolutionFamilyTwo
    | MatroxDecoderOutputResolutionFamilyThree

export interface MatroxDecoderConfig {
    outputSettings: {
        isAncEnabled: boolean
        resolution: MatroxDecoderOutputResolution
    }

    // Matrox v2.
    followInputStreamResolution?: boolean
}

export interface MatroxSdiOutputPort extends OutputPortBase {
    mode: MatroxPortMode.matroxSdi
    decoderSettings: MatroxDecoderConfig
}

export interface AsiInputPort extends PortBase {
    mode: CoaxPortMode.asi
    // Edge-specific ASI input fields (for edge-os appliances with native ristserver ASI)
    deltaSlotSizeBytes?: number
    bts?: boolean
}

export interface AsiOutputPort extends OutputPortBase {
    mode: CoaxPortMode.asi
    // Edge-specific ASI output fields (for edge-os appliances with native ristserver ASI)
    bufferDelayMs?: number
    remuxBitrate?: number // When set, enables remux mode at this bitrate. When absent, transparent passthrough.
    bts?: boolean
    rateControlPpm?: number
}

interface GroupBase {
    name: string
    applianceSecret: string
}

/**
 * @OAS_DESCRIPTION Data required when creating a new group.
 */
export interface GroupInit extends GroupBase {
    adminUsername?: string
    adminPassword?: string
}

export interface GroupUpdate extends GroupBase {}

/**
 * @OAS_DESCRIPTION A group is a grouping of related items, such as users, appliances, inputs and outputs, etc.
 */
export interface Group extends Entity, Omit<GroupBase, 'applianceSecret'> {
    applianceSecret?: string
    parent?: string

    userCount?: number
    applianceCount?: number
    interfaceCount?: number
}

export type IpInputPort =
    | UdpInputPort
    | RtpInputPort
    | RtmpInputPort
    | RistSimpleProfileInputPort
    | SrtInputPort
    | ZixiInputPort
    | GeneratorInputPort

export type CoaxInputPort = SdiInputPort | AsiInputPort

export type VideonInputPort = VideonSdiInputPort | VideonHdmiInputPort | VideonAutoInputPort

export type MatroxInputPort = MatroxSdiInputPort

export type ComprimatoInputPort = ComprimatoSdiInput | ComprimatoNdiInput
export type ComprimatoOutputPort = ComprimatoNdiOutput | ComprimatoSdiOutput

export type InputPort =
    | IpInputPort
    | CoaxInputPort
    | VideonInputPort
    | MatroxInputPort
    | ComprimatoInputPort
    | IpcInputPort
    | InternalDerivedInputSinkPort

export type IpOutputPort =
    | UdpOutputPort
    | RtpOutputPort
    | RistSimpleProfileOutputPort
    | SrtOutputPort
    | ZixiOutputPort
    | RtmpOutputPort

export type IpcOutputPort = UnixOutputPort

export type CoaxOutputPort = SdiOutputPort | AsiOutputPort

export type MatroxOutputPort = MatroxSdiOutputPort

export interface IngestTransformMixin {
    ingestTransform?: IngestTransform
}

export type OutputPort =
    | IpOutputPort
    | CoaxOutputPort
    | MatroxOutputPort
    | ComprimatoOutputPort
    | IpcOutputPort
    | InternalDerivedInputSinkPort

export enum Role {
    basic = 'basic',
    admin = 'admin',
    super = 'super',
}

export interface LoginRequest {
    username: string
    password: string
    otp?: string
}

export type LoginResult = {
    /**
     * @OAS_NAME LoggedInUser
     */
    user: {
        id: string
        object: 'user'
        username: string
        role: Role
        group: string
        mfa?: User['mfa']
        impersonatedBy?: { id: string; username: string }
        external: boolean
        /** @OAS_NAME AccessScopes */
        scopes: ACCESS_SCOPE[]
    }
}

export enum SsoStrategy {
    saml = 'saml',
    oidc = 'oidc',
}

export type LoginMethod = {
    strategy: 'local' | SsoStrategy
    displayName?: string
    displayOrder?: number
}

export type SsoLoginRequest = {
    username: string
}

export type SsoUser = {
    username: string
    effectiveRole?: Role
    effectiveGroup?: string
}

export type SamlMetadata = {
    loginUrl: string
    certificate: string
}

export type OidcMetadata = {
    issuer: string
    authorizationUrl: string
    tokenUrl: string
    userInfoUrl: string
}

export type Middleware = (req: IncomingMessage, res: ServerResponse, next: () => void) => void

export interface NewUser {
    username: string
    password: string
    role: Role
    group?: Group['id']
    /** @OAS_NAME AccessScopes */
    scopes: ACCESS_SCOPE[]
}

/**
 * @OAS_DESCRIPTION Parameters on which a list of users can be filtered.
 */
export interface UserFilter extends SearchableFilter {
    id?: string
    username?: string
    group?: string
}

export interface User {
    id: string
    username: string
    role: Role
    group: Group['id']
    mfa?: {
        totp?: {
            enabled: boolean
        }
    }
    impersonatedBy?: { id: string; username: string }
    external: boolean
    /** @OAS_NAME AccessScopes */
    scopes: ACCESS_SCOPE[]
}

// Settings configurable per user
export interface UserSettings {
    alarm: {
        notificationsEnabled: boolean
    }
    typeToConfirmEnabled: boolean
}

export interface UserUpdate extends Partial<NewUser> {
    role: Role
}

export interface BeginSystemUpgradeEvent {
    fromVersion: string
    toVersion: string
}

export interface BeginSystemUpgradeResponse {
    upgradeId: string
    logItem: AuditLog
}

export interface CompleteSystemUpgradeEvent {
    upgradeId?: string // from BeginSystemUpgradeResponse for tracking purposes
    fromVersion: string
    toVersion: string
    status:
        | { type: 'success' }
        | {
              type: 'failure'
              error: {
                  message?: string
                  [key: string]: any
              }
          }
}

export interface ApiTokenInit {
    name: string
    role: Role
    expiresAt: Date
    /** @OAS_NAME AccessScopes */
    scopes: ACCESS_SCOPE[]
}

export interface ApiToken extends Entity, ApiTokenInit {
    lastUsedAt: Date | null
}

export interface ApiTokenCreated extends ApiToken {
    token: string // Only returned once on creation
}

export interface BuildInfo {
    /**
     * @OAS_NAME ProductId
     */
    product: Product['id']
    buildTime: Date
    release: string // Release version, i.e. R3.19.0
    commit: string // Git commit hash
    pipeline: string // Pipeline id
    entitlementsPublicKey: string // Public key for verifying entitlements
    entitlementsPublicKeyHash: string // slice of the SHA256 hash of the public key
    hostPlatform?: HostPlatform
}

export type VaInputPipe =
    | VaUdpInputPipe
    | VaSdiInputPipe
    | VaAsiInputPipe
    | VaRistInputPipe
    | VaPullInputPipe
    | VaPushInputPipe
    | VaSrtInputPipe

export type VaOutputPipe =
    | VaUdpOutputPipe
    | VaSdiOutputPipe
    | VaAsiOutputPipe
    | VaRistOutputPipe
    | VaPullOutputPipe
    | VaPushOutputPipe
    | VaSrtOutputPipe

/** ############### VA OBJECT TYPES BEGIN ############### */

export type VaObject =
    | VaEdgeSinkObject
    | VaEdgeSourceObject
    | VaUdpSinkObject
    | VaUdpSourceObject
    | VaRistSinkObject
    | VaRistSourceObject
    | VaSdiInputObject
    | VaSdiOutputObject
    | VaAsiInputObject
    | VaAsiOutputObject
    | VaEncoderObject
    | VaDecoderObject
    | VaSrtSinkObject
    | VaSrtSourceObject
    | VaPullSinkObject
    | VaPushSinkObject
    | VaPullSourceObject
    | VaPushSourceObject

export interface VaBaseObject {
    inputFrom: string
    type: VaObjectInputType | VaObjectOutputType
    name: string
    logicalPort: string
}

export interface VaSdiObject extends VaBaseObject {
    type: VaObjectInputType.sdiInput | VaObjectOutputType.sdiOutput
    /**
     * @OAS_NAME VaSdiObjectStats
     */
    statistics: {
        general: GeneralStats
        netRecv: NetRecv
        netSend: NetSend
    }
}

export interface VaSdiInputObject extends VaSdiObject {
    type: VaObjectInputType.sdiInput
}

export interface VaSdiOutputObject extends VaSdiObject {
    type: VaObjectOutputType.sdiOutput
}

export interface VaAsiObject extends VaBaseObject {
    type: VaObjectInputType.asiInput | VaObjectOutputType.asiOutput
    /**
     * @OAS_NAME VaAsiObjectStats
     */
    statistics: {
        general: GeneralStats
        netRecv: NetRecv
        netSend: NetSend
    }
}

export interface VaAsiInputObject extends VaAsiObject {
    type: VaObjectInputType.asiInput
    /**
     * @OAS_NAME VaAsiInputObjectStats
     */
    statistics: VaAsiObject['statistics'] & {
        tr101: Tr101290
    }
}

export interface VaAsiOutputObject extends VaAsiObject {
    type: VaObjectOutputType.asiOutput
}

export interface VaEncoderObject extends VaBaseObject {
    type: VaObjectInputType.encoder
    /**
     * @OAS_NAME VaEncoderObjectStats
     */
    statistics: {
        curTsBitrate: number
        encFrames: number
        tr101: Tr101290
    }
}

export interface VaDecoderObject extends VaBaseObject {
    type: VaObjectOutputType.decoder
    /**
     * @OAS_NAME VaDecoderObjectStats
     */
    statistics: {
        curTsBitrate: number
        decFrames: number
        droppedFrames: number
        repeatedFrames: number
    }
}

export interface VaEdgeSinkObject extends VaBaseObject {
    type: VaObjectOutputType.edgeSink
    /**
     * @OAS_NAME VaEdgeSinkObjectStats
     */
    statistics: {
        fecRecv: FecRecv
        udpStats: UdpStats
        tr101: Tr101290
    }
}

export interface VaEdgeSourceObject extends VaBaseObject {
    type: VaObjectInputType.edgeSource
    /**
     * @OAS_NAME VaEdgeSourceObjectStats
     */
    statistics: {
        fecSend: FecSend
        udpStats: UdpStats
    }
}

export interface VaUdpSinkObject extends VaBaseObject {
    type: VaObjectInputType.udpSink
    /**
     * @OAS_NAME VaUdpSinkObjectStats
     */
    statistics: {
        fecRecv: FecRecv
        udpStats: UdpStats
        tr101: Tr101290
    }
}

export interface VaUdpSourceObject extends VaBaseObject {
    type: VaObjectOutputType.udpSource
    /**
     * @OAS_NAME VaUdpSourceObjectStats
     */
    statistics: {
        fecSend: FecSend
        udpStats: UdpStats
    }
}

/// TODO: To be removed. Rist-objects are deprecated and are scheduled for removal from Nimbra 400/VA in September 2020
export interface VaRistSinkObject extends VaBaseObject {
    type: VaObjectInputType.ristSink
    /**
     * @OAS_NAME VaRistSinkObjectStats
     */
    statistics: {
        ristRecv: RistRecv
        udpStats: UdpStats
        tr101: Tr101290
    }
}

/// TODO: To be removed. Rist-objects are deprecated and are scheduled for removal from Nimbra 400/VA in September 2020
export interface VaRistSourceObject extends VaBaseObject {
    type: VaObjectOutputType.ristSource
    /**
     * @OAS_NAME VaRistSourceObjectStats
     */
    statistics: {
        ristSend: RistSend
        udpStats: UdpStats
    }
}

export interface VaSrtSinkObject extends VaBaseObject {
    type: VaObjectInputType.srtSink
    /**
     * @OAS_NAME VaSrtSinkObjectStats
     */
    statistics: {
        srtRecv: SrtRecv
        udpStats: UdpStats
        tr101: Tr101290
    }
}

export interface VaSrtSourceObject extends VaBaseObject {
    type: VaObjectOutputType.srtSource
    /**
     * @OAS_NAME VaSrtSourceObjectStats
     */
    statistics: {
        srtSend: SrtSend
        udpStats: UdpStats
    }
}

export interface VaPullSinkObject extends VaBaseObject {
    type: VaObjectInputType.pullSink
    /**
     * @OAS_NAME VaPullSinkObjectStats
     */
    statistics: {
        general: GeneralStats
        arqRecv: ArqRecv
        arqSend: ArqSend
        fecRecv: FecRecv
        fecSend: FecSend
        netRecv: NetRecv
        netSend: NetSend
        tr101: Tr101290
    }
}

export interface VaPushSinkObject extends VaBaseObject {
    type: VaObjectInputType.pushSink
    /**
     * @OAS_NAME VaPushSinkObjectStats
     */
    statistics: {
        general: GeneralStats
        arqRecv: ArqRecv
        arqSend: ArqSend
        fecRecv: FecRecv
        fecSend: FecSend
        netRecv: NetRecv
        netSend: NetSend
        tr101: Tr101290
    }
}

export interface VaPullSourceObject extends VaBaseObject {
    type: VaObjectOutputType.pullSource
    /**
     * @OAS_NAME VaPullSourceObjectStats
     */
    statistics: {
        general: GeneralStats
        arqRecv: ArqRecv
        arqSend: ArqSend
        fecRecv: FecRecv
        fecSend: FecSend
        netRecv: NetRecv
        netSend: NetSend
    }
}

export interface VaPushSourceObject extends VaBaseObject {
    type: VaObjectOutputType.pushSource
    /**
     * @OAS_NAME VaPushSourceObjectStats
     */
    statistics: {
        general: GeneralStats
        arqRecv: ArqRecv
        arqSend: ArqSend
        fecRecv: FecRecv
        fecSend: FecSend
        netRecv: NetRecv
        netSend: NetSend
    }
}

/** ############### VA OBJECT TYPES END ############### */

/** ############### VA STATS TYPES BEGIN ############### */

export interface GeneralStats {
    /// "Reconnections" in VA GUI
    numConnects: number
    numDisconnects: number
}

export interface UdpStats {
    bitrate: number
}

export interface ArqRecv {
    recovered: number
    retransmits: number
    dropped: number
    overflow: number
    erroredSeconds: number
    retransBitrate: number
    almostDropped: number
    retransReqs: number
    duplicates: number
}

export interface ArqSend {
    retransRate: number
    retransBitrate: number
    missedReqs: number
    ignoredReqs: number
    retransPackets: number
}

export interface FecRecv {
    recovered: number
    fecBitrate: number
    fecPackets: number
    fecRate: number
}

export interface FecSend {
    fecBitrate: number
    fecPackets: number
    fecRate: number
}

export interface NetRecv {
    latency: number
    inputBitrate: number
    burstLoss: number
    receivedBytes: number
    dropped: number
    jitter: number
    jitterRatio: number
    outOfOrder: number
    overflow: number
    numPackets: number
    packetRate: number
    packetLoss: number
    erroredSeconds: number
    unavailableSeconds: number
}

export interface NetSend {
    sendBitrate: number
    sentBytes: number
    sendLimit: number
    sentPackets: number
    rtt: number
    sendErrors: number
}

/// TODO: To be removed. Rist-objects are deprecated and are scheduled for removal from Nimbra 400/VA in September 2020
export interface RistRecv {
    recovered: number
    memData: number
    memText: number
    memResident: number
    memSize: number
    dropped: number
    rtt: number
    received: number
    retransReqs: number
    duplicates: number
    unrecovered: number
}

/// TODO: To be removed. Rist-objects are deprecated and are scheduled for removal from Nimbra 400/VA in September 2020
export interface RistSend {
    resent: number
    memData: number
    memText: number
    memResident: number
    memSize: number
    rtt: number
    sent: number
}

export interface SrtRecv {
    memData: number
    memText: number
    memResident: number
    memSize: number
    dropped: number
    rtt: number
    belated: number
    received: number
    bytesLost: number
    bytesDropped: number
    bandwidth: number
    bytes: number
    lost: number
    retransmitted: number
    bitRate: number
}

export interface SrtSend {
    memData: number
    memText: number
    memResident: number
    memSize: number
    dropped: number
    rtt: number
    bytesDropped: number
    bandwidth: number
    bytes: number
    lost: number
    sent: number
    retransmitted: number
    bitRate: number
}

export interface Tr101290 {
    prio1: Tr101290Prio1
    prio2: Tr101290Prio2
}

export type Tr101290Prio1 = {
    TS_sync_loss: number
    Sync_byte_error: number
    PAT_error: number
    Continuity_count_error: number
    PMT_error: number
    PID_error: number
}
export type Tr101290Prio2 = {
    Transport_error: number
    CRC_error: number
    PCR_error: number
    PCR_accuracy_error: number
    PTS_error: number
    CAT_error: number
}

export type VaUdpInputPipe = [VaUdpSinkObject, VaEdgeSourceObject]
export type VaUdpOutputPipe = [VaEdgeSinkObject, VaUdpSourceObject]

export type VaSdiInputPipe = [VaSdiInputObject, VaEncoderObject, VaEdgeSourceObject]
export type VaSdiOutputPipe = [VaEdgeSinkObject, VaDecoderObject, VaSdiOutputObject]

export type VaAsiInputPipe = [VaAsiInputObject, VaEdgeSourceObject]
export type VaAsiOutputPipe = [VaEdgeSinkObject, VaAsiOutputObject]

export type VaPullInputPipe = [VaPullSinkObject, VaEdgeSourceObject]
export type VaPullOutputPipe = [VaEdgeSinkObject, VaPullSourceObject]

export type VaPushInputPipe = [VaPushSinkObject, VaEdgeSourceObject]
export type VaPushOutputPipe = [VaEdgeSinkObject, VaPushSourceObject]

export type VaSrtInputPipe = [VaSrtSinkObject, VaEdgeSourceObject]
export type VaSrtOutputPipe = [VaEdgeSinkObject, VaSrtSourceObject]

/// TODO: To be removed. Rist-objects are deprecated and are scheduled for removal from Nimbra 400/VA in September 2020
export type VaRistInputPipe = [VaRistSinkObject, VaEdgeSourceObject]
export type VaRistOutputPipe = [VaEdgeSinkObject, VaRistSourceObject]

export enum VaObjectInputType {
    sdiInput = 'sdi-input',
    encoder = 'encoder',
    udpSink = 'udp-sink',
    ristSink = 'rist-sink',
    srtSink = 'srt-sink',
    edgeSource = 'edge-source',
    asiInput = 'asi-input',
    pullSink = 'pull-sink',
    pushSink = 'push-sink',
}

export enum VaObjectOutputType {
    sdiOutput = 'sdi-output',
    decoder = 'decoder',
    udpSource = 'udp-source',
    ristSource = 'rist-source',
    srtSource = 'srt-source',
    edgeSink = 'edge-sink',
    asiOutput = 'asi-output',
    pullSource = 'pull-source',
    pushSource = 'push-source',
}

/** ############### VA STATS TYPES END ############### */

export type StringPropsAsNumbers<T> = {
    [k in keyof T]: k extends string ? number : never
}

export interface Tr101290Metrics {
    prio1: StringPropsAsNumbers<Tr101290Prio1>
    prio2: StringPropsAsNumbers<Tr101290Prio2>
    time: Date
    window: MetricWindow
    applianceId: string
    applianceName: string
    applianceType: string
    inputId: string
    channelId: number
    inputName: string
    type: string
}

/**
 * @OAS_DESCRIPTION All metrics related to the input until
 * it reaches the first core video nodes. Note that the
 * vaObjects property is likely to change in upcoming revisions
 * to the API.
 */
export interface InputMetrics {
    vaObjects?: VaObject[]
    ristMetrics: StreamMetrics[]
    tr101290Metrics: Tr101290Metrics[]
}

/**
 * RistMetricType: the possible types of metric objects emitted from the ristServer
 */
export enum RistMetricType {
    ristInput = 'ristInput',
    ristOutput = 'ristOutput',
    udpInput = 'udpInput',
    udpOutput = 'udpOutput',
    unixInput = 'unixInput',
    unixOutput = 'unixOutput',
    rtpInput = 'rtpInput',
    rtpOutput = 'rtpOutput',
    sdiInput = 'sdiInput',
    sdiOutput = 'sdiOutput',
    srtInput = 'srtInput',
    srtOutput = 'srtOutput',
    internalInput = 'internalInput', //internalDerivedInputSource
    internalOutput = 'internalOutput', //internalDerivedInputSink
    asiInput = 'asiInput',
    asiOutput = 'asiOutput',
    channel = 'channel',
}

export enum ZixiMetricType {
    zixiOutput = 'zixiOutput',
    zixiInput = 'zixiInput',
}

/** @deprecated For srt-live-transmit. Use RistMetricType */
export enum SrtMetricType {
    srtOutput = 'srtOutput',
    srtInput = 'srtInput',
}

export enum RtmpMetricType {
    rtmpInput = 'rtmpInput',
    rtmpOutput = 'rtmpOutput',
}

export enum TrancodeStreamMetricType {
    transcodeStream = 'transcodeStream',
}

export enum MptsMetricType {
    mptsDemux = 'mptsDemux',
    // mptsInput = 'mptsInput',
    // mptsOutput = 'mptsOutput',
}

export enum RistSimpleProfileMetricType {
    ristSimpleInput = 'ristSimpleInput',
    ristSimpleOutput = 'ristSimpleOutput',
}

export enum VideonMetricType {
    'videonAutoInput' = 'videonAutoInput',
    'videonHdmiInput' = 'videonHdmiInput',
    'videonSdiInput' = 'videonSdiInput',
}

export enum SdiMetricType {
    'sdiInput' = 'sdiInput',
    'sdiOutput' = 'sdiOutput',
}

export enum AsiMetricType {
    'asiInput' = 'asiInput',
    'asiOutput' = 'asiOutput',
}

export enum MatroxMetricType {
    'matroxSdiInput' = 'matroxSdiInput',
    'matroxSdiOutput' = 'matroxSdiOutput',
}

export enum ComprimatoMetricType {
    'comprimatoSdiInput' = 'comprimatoSdiInput',
    'comprimatoNdiInput' = 'comprimatoNdiInput',
    'comprimatoNdiOutput' = 'comprimatoNdiOutput',
    'comprimatoSdiOutput' = 'comprimatoSdiOutput',
}

export type StreamMetricType =
    | RistMetricType
    | RistSimpleProfileMetricType
    | ZixiMetricType
    | SrtMetricType
    | RtmpMetricType
    | MptsMetricType
    | TrancodeStreamMetricType

export type RistOutputMetricType =
    | RistMetricType.ristInput
    | RistMetricType.ristOutput
    | RistMetricType.udpOutput // type 'udpOutput' also covers SRT and Zixi outputs, since they are fetched from the ristserver as udp outputs streams and then fed to zixi/srt processes
    | RistMetricType.rtpOutput
    | RistSimpleProfileMetricType.ristSimpleOutput

export interface ChannelMetrics extends StreamMeasurement<RistMetricType.channel> {
    retransmitBudget: number
    retransmitRoundtripMs: number
    resetCount: number
    maxReorderTimeMs: number
    state: ChannelState | null // Introduced in R3.15.0. Will always be 'null' for appliances running older versions. Null could be removed in R3.16.0
}

export interface RistInputWindow1mMetrics extends StreamMeasurement<RistMetricType.ristInput, MetricWindow.m1> {
    packetsLost: number
}

export interface RistSimpleProfileInputMetrics extends StreamMeasurement<RistSimpleProfileMetricType.ristSimpleInput> {
    packetsLost: number
    bytesReceived: number
    packetsReceived: number
    receiveBitrate: number
    receivePacketrate: number
    rtpPacketsReceived: number
    retransmitReceivePacketrate: number
    retransmitReceiveBitrate: number
    packetSendErrors: number
    packetsDiscarded: number
    roundtripMs: number
    propagationDelayMs: number
    longestBurstLoss: number
    unsupportedRtcpPacketsReceived: number
    malformedRtcpPacketsReceived: number
    multipathState?: RistInputMultipathState
}

export interface RistSimpleInputWindow1mMetrics extends StreamMeasurement<
    RistSimpleProfileMetricType.ristSimpleInput,
    MetricWindow.m1
> {
    packetsLost: number
}

export interface RistInputMetrics extends StreamMeasurement<RistMetricType.ristInput> {
    packetSendErrors: number
    packetsDiscarded: number
    unsupportedRtcpPacketsReceived: number
    packetsLost: number
    interarrivalJitter?: number
    bytesReceived: number
    packetsReceived: number
    receiveBitrate: number
    receivePacketrate: number
    rtpPacketsReceived: number
    retransmitReceivePacketrate: number
    retransmitReceiveBitrate: number
    roundtripMs: number
    propagationDelayMs: number
    longestBurstLoss: number
    malformedRtcpPacketsReceived: number
    multipathState?: RistInputMultipathState
}

export interface RtpInputMetrics extends StreamMeasurement<RistMetricType.rtpInput> {
    packetsDiscarded: number
    receiveBitrate: number
    receivePacketrate: number
    fecReceivePacketrate?: number
    packetsRecovered?: number
    unrecoveredPacketrate?: number
    packetsLost?: number
    lostPacketrate?: number
    timestampViolations: number
    ssrc?: number
}

export interface RtpOutputMetrics extends StreamMeasurement<RistMetricType.rtpOutput> {
    packetsLost: number
    sendBitrate: number
    sendPacketrate: number
    lostPacketrate: number
    isEgress: true
    packetsDroppedBecauseOfBitrateLimit?: number
}

export interface UdpInputMetrics extends StreamMeasurement<RistMetricType.udpInput> {
    // Number of malformed *RTP* packets. Not included in `packetsReceived`.
    packetsDiscarded: number
    // Number of packets discarded because the input wasn't active.
    packetsWhileInactive: number
    receiveBitrate: number
    receivePacketrate: number
    status?: UdpInputStatusCode
}

export interface UdpOutputMetrics extends StreamMeasurement<RistMetricType.udpOutput> {
    packetsLost: number
    packetsDropped: number
    sendBitrate: number
    sendPacketrate: number
    lostPacketrate: number
    playoutMarginMs: number
    status?: UdpOutputStatusCode
    packetsDroppedBecauseOfBitrateLimit?: number
}

export interface UnixInputMetrics extends StreamMeasurement<RistMetricType.unixInput> {
    // Number of malformed *RTP* packets. Not included in `packetsReceived`.
    packetsDiscarded: number
    // Number of packets discarded because the input wasn't active.
    packetsWhileInactive: number
    receiveBitrate: number
    receivePacketrate: number
    status?: UdpInputStatusCode
}

export interface UnixOutputMetrics extends StreamMeasurement<RistMetricType.unixOutput> {
    packetsLost: number
    sendBitrate: number
    sendPacketrate: number
    lostPacketrate: number
    playoutMarginMs: number
    status?: UdpOutputStatusCode
    packetsDroppedBecauseOfBitrateLimit?: number
}

export interface SdiInputMetrics extends StreamMeasurement<RistMetricType.sdiInput> {
    framesReceived: number
    framesDropped: number
    framesDroppedDueToFormatChanged?: number
    framesDroppedDueToHardwareError?: number
    framesDroppedDueToSignalLoss?: number
    framesWithInjectedSilence?: number
    framesOverrun: number
    encodeOverruns: number
    pendingMuxedPackets: number
    packetsSent: number
    packetsDropped: number
    status?: SdiInputStatusCode
}

export interface SdiOutputMetrics extends StreamMeasurement<RistMetricType.sdiOutput> {
    framesSent: number
    framesDropped: number
    framesDroppedDueToAligner: number
    framesDroppedDueToHardwareError: number
    framesDroppedDueToOverflow: number
    framesRepeated: number
    ancDataDroppedDueToOverflow: number
    decoderRecvVideo: number
    decoderRecvAudio: number
    decoderRecvAnc: number
    decoderRecvUnknownPes: number
    decoderRecvOther: number
    decodedVideoFrames: number
    decoderSendErrors: number
    decoderReceiveErrors: number
    decoderResets: number
    decoderTransferErrors: number
    decoderRawAudioErrors: number
    configureAttempts: number
    configureFailures: number
    alignerResets: number
    ntpLatencyMs?: number
    alignerBufferLevel?: number
    alignerAvDelay?: number
    alignerJitterDelay?: number
    alignerMsaDelay?: number
    status?: SdiOutputStatusCode
    lastFailedStage?: SdiOutputLastFailedStage
    audioChannelsRequested?: number
    audioChannelsRouted?: number
    audioChannelsDroppedUnknownPid?: number
    audioChannelsDroppedInvalidFormat?: number
}

export interface InternalInputMetrics extends StreamMeasurement<RistMetricType.internalInput> {
    bytesWhileInactive: number
    packetReceiveErrors: number
    timestampViolations: number
    sequenceNumberViolations?: number
    sequenceNumberOverruns?: number
    packetsDropped: number
    packetsDiscarded: number
    packetsWhileInactive: number
    bytesReceived: number
    receiveBitrate?: number
    packetsReceived: number
    receivePacketrate?: number
    packetsLost: number
    streamId: number
}

export interface InternalOutputMetrics extends StreamMeasurement<RistMetricType.internalOutput> {
    fecPacketsDropped: number
    socketError: number
    bytesSent: number
    sendBitrate?: number
    packetsLost: number
    lostPacketrate?: number
    packetsRecovered: number
    packetsSent: number
    sendPacketrate?: number
    playoutMarginMs: number
    fecBytesSent?: number
    fecSendBitrate?: number
    fecPacketsSent?: number
    fecSendPacketrate?: number
    streamId: number
    packetsDropped: number
    packetsDroppedBecauseOfBitrateLimit: number
}

export interface AsiInputMetrics extends StreamMeasurement<RistMetricType.asiInput> {
    bytesReceived: number
    receiveBitrate: number
    bytesDropped: number
    droppedBitrate: number
    status?: AsiInputStatusCode
}

export interface AsiOutputMetrics extends StreamMeasurement<RistMetricType.asiOutput> {
    bytesSent: number
    sendBitrate: number
    bytesDropped: number
    bytesDroppedDueToOverflow?: number
    bytesDroppedDueToHardwareError?: number
    bytesDroppedDueToLostPlayback?: number
    droppedBitrate: number
    underrunCount: number
    syncErrorCount: number
    reconfigureCount?: number
    status?: AsiOutputStatusCode
    adjustmentPpm?: number
    bufferCapacityBytes?: number
    bufferFillBytes?: number
    estimatedInputBitrateBps?: number
    outputBitrateBps?: number
}

export interface UdpOutput1mMetrics extends StreamMeasurement<RistMetricType.udpOutput, MetricWindow.m1> {
    packetsLost: number
}

export interface UnixOutput1mMetrics extends StreamMeasurement<RistMetricType.unixOutput, MetricWindow.m1> {
    packetsLost: number
}

export interface RistOutputMetrics extends StreamMeasurement<RistMetricType.ristOutput> {
    bytesSent: number
    packetSendErrors: number
    packetsSent: number
    sendBitrate: number
    sendPacketrate: number
    retransmitSendPacketrate: number
    retransmitSendBitrate: number
    rtpPacketsSent: number
    malformedRtcpPacketsReceived: number
    unsupportedRtcpPacketsReceived: number
    reportedPacketLossPercent: number
    rtpPacketsDroppedBecauseOfBitrateLimit: number
    roundtripMs: number

    multipathState?: RistOutputMultipathState
}

export interface RistSimpleProfileOutputMetrics extends StreamMeasurement<RistSimpleProfileMetricType.ristSimpleOutput> {
    bytesSent: number
    packetSendErrors: number
    packetsSent: number
    sendBitrate: number
    sendPacketrate: number
    retransmitSendBitrate: number
    retransmitSendPacketrate: number
    roundtripMs: number
    rtpPacketsSent: number
    rtcpReceivePacketrate: number
    malformedRtcpPacketsReceived: number
    unsupportedRtcpPacketsReceived: number
    reportedPacketLossPercent: number
    rtpPacketsDroppedBecauseOfBitrateLimit: number
    multipathState?: RistOutputMultipathState

    isEgress: true
}

export interface ZixiOutputMetrics extends StreamMeasurement<ZixiMetricType.zixiOutput, MetricWindow.s10> {
    bitrate: number
    connectionStatus: string
    error?: string

    // The following fields are only set when using the zixi broadcaster
    zixiBitrate?: number
    droppedPackets?: number
    duplicatePackets?: number
    sendPacketRate?: number
    jitter?: number
    packetRate?: number
    packets?: number
    rtt?: number

    arqLostPackets?: number
    arqDuplicatePackets?: number
    arqRecoveredPackets?: number
    arqOverflowedPackets?: number

    fecSentPackets?: number
    fecRecoveredPackets?: number
    fecReceivedPackets?: number
}

export interface ZixiInputMetrics extends StreamMeasurement<ZixiMetricType.zixiInput, MetricWindow.s10> {
    bitrate: number
    rtt: number
    connectionStatus: string
}

interface RistSrtMetrics extends Pick<
    SrtStats,
    'srtConnectionId' | 'connectionAttempts' | 'socketResets' | 'receiveErrors' | 'sendErrors'
> {
    localEndpoint: SrtStats['sockets'][number]['links'][number]['localEndpoint']
    remoteEndpoint: SrtStats['sockets'][number]['links'][number]['remoteEndpoint']
    packets: number
    bytes: number
    packetsDropped: number
    packetsLost: number
    retransmitPacketsRequested: number
    receiveBitrate: number
    sendBitrate: number
}

/** @deprecated For srt-live-transmit */
export interface SrtLiveTransmitInputMetrics extends StreamMeasurement<SrtMetricType.srtInput, MetricWindow.s10> {
    bitrate: number
    rtt: number
    remoteAddress?: string
    packetsDropped: number
    packetsLost: number
    packetsRetransmitted: number
    msRecvBuffer: number
    connectionStatus?: SrtConnectionStatus
}

export interface RistSrtInputMetrics
    extends StreamMeasurement<RistMetricType.srtInput, MetricWindow.s10>, RistSrtMetrics {
    /** @deprecated For compat with srt-live-transmit. Use receiveBitrate and sendBitrate instead. */
    bitrate: number
    /** @deprecated For compat with srt-live-transmit */
    connectionStatus: SrtConnectionStatus
}

export type SrtInputMetrics = SrtLiveTransmitInputMetrics | RistSrtInputMetrics

/** @deprecated For srt-live-transmit */
export interface SrtLiveTransmitOutputMetrics extends StreamMeasurement<SrtMetricType.srtOutput, MetricWindow.s10> {
    bitrate: number
    rtt: number
    remoteAddress?: string
    packetsDropped: number
    packetsRetransmitted: number
    msSendBuffer: number
    connectionStatus?: SrtConnectionStatus
}

export interface RistSrtOutputMetrics
    extends StreamMeasurement<RistMetricType.srtOutput, MetricWindow.s10>, RistSrtMetrics {
    isEgress: true
    /** @deprecated For compat with srt-live-transmit. Use receiveBitrate and sendBitrate instead. */
    bitrate: number
    /** @deprecated For compat with srt-live-transmit */
    connectionStatus: SrtConnectionStatus
}

export type SrtOutputMetrics = SrtLiveTransmitOutputMetrics | RistSrtOutputMetrics

export interface RtmpInputMetrics extends StreamMeasurement<RtmpMetricType.rtmpInput, MetricWindow.s10> {
    receiveBitrateKbps: number
}

export interface RtmpOutputMetrics extends StreamMeasurement<RtmpMetricType.rtmpOutput, MetricWindow.s10> {
    state: RtmpOutputState
    restartCount: number
    sendBitrateKbps: number
}

export interface MptsDemuxMetrics extends StreamMeasurement<MptsMetricType.mptsDemux, MetricWindow.s10> {
    parentInputId: string
    parentStreamId: number

    parentUdpPackets: number
    parentUdpPacketsRate: number
    parentTruncatedBytes: number
    parentTruncatedBytesRate: number
    parentTsPackets: number
    parentTsPacketsRate: number

    udpPackets: number
    udpPacketsRate: number
    tsPackets: number
    tsPacketsRate: number
}

export interface TranscodeStreamMetrics extends StreamMeasurement<
    TrancodeStreamMetricType.transcodeStream,
    MetricWindow.s10
> {
    sendBitrateKbps: number
}

export enum MetricWindow {
    s10 = '10 seconds',
    m1 = '1 minute',
    m15 = '15 minutes',
    h1 = '1 hour',
    h24 = '24 hours',
}

export interface StreamMeasurement<T extends StreamMetricType, TWindow extends MetricWindow = MetricWindow.s10> {
    sampledAt: Date
    applianceId: string
    applianceName: string
    applianceType: string
    streamId?: number
    type: T
    window: TWindow
    inputId: string
    channelId: number
    /// Note 13/11/2020: 'isEgress' is only applicable for 'ristServer' types (i.e. other egress-nodes such as the zixi are not tagged with this)
    // i.e. isEgress means the last node leaving the ristServer.
    isEgress?: boolean
    outputId?: string
    serverApplianceId?: string
    clientApplianceId?: string
}

export interface RistMeasurement<
    T extends RistMetricType | RistSimpleProfileMetricType,
    TWindow extends MetricWindow = MetricWindow.s10,
> extends StreamMeasurement<T, TWindow> {}

export type AnyRistServerInputMetric = RistInputMetrics | RistServerIngressMetric
export type AnyRistServerOutputMetric = RistOutputMetrics | RistServerEgressMetric

export type AnyRistServerPortMode = IpPortMode.udp | IpPortMode.rtp | IpPortMode.rist | IpPortMode.srt
export type NonRistServerOutputMetrics = RtmpOutputMetrics | ZixiOutputMetrics | SrtLiveTransmitOutputMetrics

export type StreamOutputMetrics =
    | NonRistServerOutputMetrics
    | AnyRistServerOutputMetric
    | UdpOutput1mMetrics
    | UnixOutput1mMetrics
    | InternalInputMetrics
    | InternalOutputMetrics

export type StreamMetrics =
    | AnyRistServerInputMetric
    | RtmpInputMetrics
    | ChannelMetrics
    | SrtLiveTransmitInputMetrics
    | ZixiInputMetrics
    | RistInputWindow1mMetrics
    | RistSimpleInputWindow1mMetrics
    | StreamOutputMetrics
    | MptsDemuxMetrics
    | TranscodeStreamMetrics

export type RistServerIngressMetric =
    | RistSimpleProfileInputMetrics
    | RtpInputMetrics
    | RistSrtInputMetrics
    | UdpInputMetrics
    | UnixInputMetrics
    | InternalInputMetrics
    | AsiInputMetrics
    | SdiInputMetrics

export type RistServerEgressMetric =
    | RistSimpleProfileOutputMetrics
    | RtpOutputMetrics
    | RistSrtOutputMetrics
    | UdpOutputMetrics
    | UnixOutputMetrics
    | SdiOutputMetrics
    | InternalOutputMetrics
    | AsiOutputMetrics

/**
 * @OAS_DESCRIPTION All metrics related to the output from the
 * point where it leaves the first core video node. Note that the
 * vaObjects property is likely to change in upcoming revisions
 * to the API.
 */
export interface OutputMetrics {
    vaObjects?: VaObject[]
    ristMetrics: StreamMetrics[]
    tr101290Metrics: Tr101290Metrics[]
}

export type Timestamp = string
export type Bitrate = number
export type BitrateSample = [Timestamp, Bitrate]

export interface MaxBitrates {
    inputMaxBitrates: BitrateSample[]
    outputMaxBitrates: BitrateSample[]
}

/**
 * @OAS_DESCRIPTION A single sample in a numeric metrics time-series. `time` is the epoch-millisecond
 * start of the bucket; `value` is the aggregated value, or null when the bucket has no data (a gap).
 */
export interface MetricsTimeSeriesPoint {
    time: number
    value: number | null
}

/**
 * @OAS_DESCRIPTION A named numeric time-series (e.g. ingress bitrate) bucketed over the requested range.
 * `key` is unique per response and identifies the ClickHouse column the series came from.
 */
export interface MetricsTimeSeries {
    key: string
    points: MetricsTimeSeriesPoint[]
}

/**
 * @OAS_DESCRIPTION A single sample in a status timeline. `time` is the epoch-millisecond start of the bucket;
 * `status` is the distinct status value(s) observed in that bucket (comma-separated if more than one).
 */
export interface MetricsStatusPoint {
    time: number
    status: string
}

/**
 * @OAS_DESCRIPTION A named status timeline (e.g. UDP output status) showing the discrete state over the
 * requested range, suitable for a status-history visualisation.
 */
export interface MetricsStatusTimeline {
    key: string
    points: MetricsStatusPoint[]
}

/**
 * @OAS_DESCRIPTION Time-series metrics for a single stream over a time range, used to render in-app graphs in
 * the Network Manager (an alternative to Grafana that is scoped to the requesting user's group). `from`/`to`
 * are the resolved range in epoch milliseconds and `stepSeconds` is the bucket size that was applied.
 */
export interface MetricsSeriesResponse {
    from: number
    to: number
    stepSeconds: number
    series: MetricsTimeSeries[]
    statusTimelines: MetricsStatusTimeline[]
}

/**
 * @OAS_DESCRIPTION Query parameters for the input metrics-series endpoint. `table` selects the ClickHouse
 * `telegraf` table for the stream type (e.g. `ristSimpleInput`, `edgeSdiOutput`, `tr101290`); `streamId`,
 * `channelId` and `applianceId` pin the exact stream shown in the Service Overview (matching how the live
 * snapshot is filtered). `metrics` is a comma-separated list of columns to return data for — when omitted no
 * series are returned. `from`/`to` accept an ISO-8601 timestamp or an epoch-millisecond string and default to
 * a trailing one-hour window; `stepSeconds` optionally overrides the server-chosen bucket size.
 */
export interface MetricsSeriesFilter {
    from?: string
    to?: string
    stepSeconds?: number
    table?: string
    streamId?: string
    channelId?: string
    applianceId?: string
    metrics?: string
}

export interface GroupInputPermission {
    type?: 'group'
    groupId: Group['id']
    accessType: InputAccessType
}

export interface GroupListInputPermission {
    type: 'groupList'
    groupListId: GroupRecipientList['id']
    accessType: InputAccessType
}

/**
 * @OAS_DESCRIPTION Describes the kind of access a group has to an input.
 * * View: 1
 * * Pull: 2
 */
export enum InputAccessType {
    view = 1,
    pull = 2,
}

export enum ExpFeatures {
    // These currently need to be  abc = 'abc' and not abc = 'friendly name for abc'
    OASResponseValidation = 'OASResponseValidation',
    ExtSelectableDelayMode = 'ExtSelectableDelayMode',
    ExtHealthAlarms = 'ExtHealthAlarms',
    AdInsertion = 'AdInsertion',
    FramerateConversion = 'FramerateConversion',
    ExtPidRemapping = 'ExtPidRemapping',
    ExtUnifiedBilling = 'ExtUnifiedBilling',
    ExtAlarmKafkaNotifications = 'ExtAlarmKafkaNotifications',
    AvcSdiEncoding = 'AvcSdiEncoding',
}

export type ExperimentalFeatures = Partial<Record<ExpFeatures, boolean>>

/**
 * Entry in the SSO group mappings list.
 * Each entry maps a single IDP group to an Edge role and group with priority ordering.
 */
export type GroupMappingEntry = {
    /** Priority index for evaluation order. Lower = higher priority. */
    index: number
    idpGroup: string
    edgeRole: Role
    /** Edge group ID that a user is mapped to (see {@link Group})*/
    edgeGroup: string
}

export type SsoStrategySettings = {
    enabled?: boolean
    displayName?: string
    displayOrder?: number
    emailClaim?: string
    groupsClaim?: string
    /**
     * List of SSO group mappings with priority ordering.
     * Each entry maps a single IDP group to an Edge role and group.
     * Lower index = higher priority (first match wins).
     * IDP groups must be unique in the list.
     *
     * @example
     * [
     *   { index: 0, idpGroup: "cn=admins,ou=groups,dc=corp", edgeRole: "admin", edgeGroup: "system-group-id" },
     *   { index: 1, idpGroup: "developers", edgeRole: "basic", edgeGroup: "dev-team-id" }
     * ]
     */
    groupMappings?: GroupMappingEntry[]

    configSource?: SsoConfigSource
    metadataUrl?: string
}

export type SsoSettingsSaml = SsoStrategySettings & {
    loginUrl?: string
    providerId?: string
    certificate?: string

    forceAuthentication?: boolean
    acceptedClockSkewMs?: number

    // Signing settings
    signedAuthnRequests?: boolean
    signedAssertions?: boolean
    publicCertificate?: string
    privateKey?: string
    signatureAlgorithm?: string
    digestAlgorithm?: string
}

export enum OidcPrompt {
    none = 'none',
    login = 'login',
    select_account = 'select_account',
}

export type SsoSettingsOidc = SsoStrategySettings & {
    issuer?: string
    userInfoUrl?: string
    authorizationUrl?: string
    tokenUrl?: string
    clientId?: string
    clientSecret?: string
    scope?: string

    acrValues?: string
    prompt?: OidcPrompt
}

export enum SsoConfigSource {
    metadata = 'metadata',
    manual = 'manual',
}

export type Sso = {
    saml?: SsoSettingsSaml
    oidc?: SsoSettingsOidc
}

export enum HostPlatform {
    edgeos = 'edgeos',
}

export enum KubernetesProvider {
    metal = 'metal',
    alibaba = 'alibaba', // eks
    aws = 'aws', // eks
    google = 'google',
    azure = 'azure', // aks
    oracle = 'oracle', //oks
}

export enum TranscoderType {
    nvidia = 'nvidia',
    netint = 'netint',
    quicksync = 'quicksync',
    videotoolbox = 'videotoolbox',
    cpu = 'cpu',
}

export interface Transcoder {
    type: TranscoderType
    /** Driver version (e.g., "580.95.05" for NVIDIA) */
    driver?: string
    /** List of device/GPU model names (e.g., ["NVIDIA RTX PRO 4000 Blackwell", "NVIDIA RTX PRO 4000 Blackwell"]) */
    devices?: string[]
    /** GPU architecture (e.g., "Blackwell", "Ampere") - NVIDIA only */
    architecture?: string
}

export type TranscodeEncoderName =
    | 'h264_nvenc'
    | 'hevc_nvenc'
    | 'h264_ni_quadra_enc'
    | 'h265_ni_quadra_enc'
    | 'h264_qsv'
    | 'hevc_qsv'
    | 'h264_videotoolbox'
    | 'hevc_videotoolbox'
    | 'libx264'
    | 'libx265'
    | 'mpeg2video'

export enum BroadcastStandard {
    dvb = 'dvb',
    atsc = 'atsc',
}

export interface RtuEntitlements {
    rtus?: {
        [serviceType: string]: number
    }
}

export interface ConnectitEntitlements extends RtuEntitlements {
    product: ConnectitProductId
    maxTokens: number
    expiresAt?: Date
}

export interface NimbraEdgeEntitlements extends RtuEntitlements {
    product: NimbraEdgeProductId
    /**
     * When true, the installation may run frame-rate conversion (FrameFormer) at full quality
     * without the demo watermark. Absent/false means only demo-mode (watermarked) conversion is allowed.
     */
    framerateConversionLicensed?: boolean
}

export type LicenseEntitlements = ConnectitEntitlements | NimbraEdgeEntitlements

export enum Nimbra400AlarmsFilter {
    edgeManagedServices = 'edgeManagedServices',
    all = 'all',
    none = 'none',
}

export interface GlobalSettings {
    kubernetesProvider?: KubernetesProvider
    vaUpdateUrl: string
    logLevel: LogLevel

    /**
     * @OAS_EXCLUDED
     *
     * ristTunnelBasePort is excluded until port conflict validation takes this property into account rather than the hard coded 20_000 range.
     */
    ristTunnelBasePort: number
    defaultDelay: number
    defaultBroadcastStandard: BroadcastStandard
    defaultHandoverMethod: HandoverMethod
    zixiZecKey: string
    ntpServer: string
    ntpEnabled: boolean
    entitlements: LicenseEntitlements
    /**
     * @deprecated Deprecated since R3.21.0 and not used. True corresponds to nimbra400Alarms: 'all' and false to nimbra400Alarms: 'edgeManagedServices'.
     */
    showAllNimbra400Alarms?: boolean
    // nimbra400Alarms can be required from R3.22.0
    nimbra400Alarms?: Nimbra400AlarmsFilter
    sslRedirectEnabled: boolean
    expFeatures: ExperimentalFeatures
    customTabTitle: string
    // Whether to show alarm popups in the Network Manager.
    alarmNotificationsEnabled: boolean
    sso: Sso
    adBucketSizeThreshold: string
    backup: BackupConf
    useEphemeralTunnelSenderPortsForNewAppliances: boolean
}

export type GlobalSettingsUpdate = Omit<GlobalSettings, 'entitlements'>

export type BackupInit = {
    type: 'config' | 'billing' | 'metrics'
}

export interface S3Config {
    endpoint: string
    region?: string
    accessKeyId: string
    secretAccessKey: string
}

export type S3ConfigWithPath = S3Config & {
    objectPath: string
}

export type ExternalS3BackupConfig = S3ConfigWithPath & {
    takeExternalS3Backups: boolean
}

export type ExternalS3BackupConfigSerialized = S3ConfigWithPath & {
    takeExternalS3Backups: string
}

export interface BackupConf {
    externalS3?: ExternalS3BackupConfig
}

/** Cluster role for continuous recovery, derived from CNPG replica config. */
export type RecoveryRole = 'primary' | 'standby'

/**
 * Archiving posture:
 * - `disabled` — WAL archiving off.
 * - `active-no-backup` — archiving on but no base backup yet, so nothing is
 *   restorable (the silent-failure case worth warning about).
 * - `active` — archiving on with at least one base backup.
 */
export type RecoveryArchivingState = 'disabled' | 'active-no-backup' | 'active'

/**
 * Continuous recovery (CNPG WAL archiving + base backups) config exposed to NM.
 * Field shape mirrors External S3 backups (`endpoint` + `objectPath`) so the two
 * settings forms feel consistent; the backend assembles `s3://${objectPath}` into
 * the underlying ObjectStore CR. Credentials are returned as-is from the K8s Secret
 * (same pattern as External S3) so the form can pre-populate and the Test
 * Connection button keeps working after save.
 */
export interface RecoveryConfig {
    /** Read-only: whether continuous recovery can be configured at all — true when the
     *  barman-cloud plugin is installed on the cluster (Cluster.spec.plugins[barman].enabled).
     *  When false, the controls are unavailable: enable cert-manager / managed certs and
     *  re-run the installer. */
    available: boolean
    /** Mirrors Cluster.spec.plugins[barman-cloud].isWALArchiver. */
    enabled: boolean
    /** Read-only: a human-readable problem with the most recent base backup or with WAL
     *  archiving (upload), when continuous recovery is enabled. Absent when healthy. */
    errorMessage?: string
    /** Read-only: primary vs standby (CNPG replica mode). */
    role: RecoveryRole
    /** Read-only: derived archiving posture (see RecoveryArchivingState). */
    state: RecoveryArchivingState
    /** S3 endpoint URL, e.g. `https://s3.eu-north-1.amazonaws.com`. */
    endpoint: string
    /** S3 region. Optional: leave blank for AWS us-east-1 / `auto` (Cloudflare R2) /
     *  MinIO; required for non-us-east-1 AWS buckets (SigV4 signing) and for Garage
     *  (must match its configured `s3_region`). */
    region?: string
    /** Bucket plus optional subpath, e.g. `my-bucket/edge`. */
    objectPath: string
    /** ObjectStore.spec.retentionPolicy, e.g. `7d`. */
    retentionPolicy?: string
    accessKeyId: string
    secretAccessKey: string
    /** Read-only status from the ObjectStore recovery window. ISO timestamp of the
     *  most recent successful base backup; absent until the first one completes. */
    lastSuccessfulBackupTime?: string
    /** Read-only status: oldest point the archive can restore to (base backup +
     *  WAL). Absent until the first base backup completes. */
    firstRecoverabilityPoint?: string
    /** Read-only: the `app` DB password (from edge-cnpg-app). Surfaced so an
     *  operator can seed the same value on a standby (it's the replicated DB's
     *  password). Absent if the secret isn't present. */
    appDbPassword?: string
}

/** Update payload — config fields only; status fields are read-only and ignored on write. */
export type RecoveryConfigUpdate = Omit<
    RecoveryConfig,
    | 'available'
    | 'errorMessage'
    | 'lastSuccessfulBackupTime'
    | 'firstRecoverabilityPoint'
    | 'role'
    | 'state'
    | 'appDbPassword'
>

export type BackupType = 'config'

export interface BackupListItem {
    key: string
    filename: string
    type: BackupType
    sizeBytes: number
    lastModified: string
    local: boolean
}

export interface ExternalS3BackupList {
    configured: boolean
    items: BackupListItem[]
}

export interface BackupPullInit {
    key: string
}

export interface BackupPullCreated {
    jobName: string
}

export type BackupPullPhase = 'pending' | 'running' | 'succeeded' | 'failed'

export interface BackupPullJob {
    jobName: string
    key: string
    filename: string
    phase: BackupPullPhase
    error?: string
    startedAt?: string
}

export interface BackupPullStatus {
    jobs: BackupPullJob[]
}

export interface BackupRestoreInit {
    filename: string
}

export interface BackupRestoreCreated {
    jobName: string
}

export type BackupRestorePhase = 'pending' | 'running' | 'succeeded' | 'failed'

export interface BackupRestoreJob {
    jobName: string
    filename: string
    phase: BackupRestorePhase
    error?: string
    startedAt?: string
    finishedAt?: string
}

export interface BackupRestoreStatus {
    jobs: BackupRestoreJob[]
}

export interface BackupUploadInit {
    /** Backup file contents, base64-encoded (gzipped pg_dump). */
    contents: string
}

export interface BackupUploadResult {
    /** Filename the backup was saved as on the Edge installation. */
    filename: string
}

export type LocalBackupType = 'config' | 'billing' | 'metrics'

export interface LocalBackupListItem {
    filename: string
    type: LocalBackupType
    sizeBytes: number
    lastModified: string
    restorable: boolean
}

export interface LocalBackupList {
    items: LocalBackupListItem[]
}

type UploadRequestHeaders = {
    'content-length'?: string | string[]
}
// UploadRequestStreamLike is defined to narrow down IncomingMessage type so that
// OAS generation does not expand Node's IncomingMessage internals and bloat json schema
export type UploadRequestStreamLike = { headers: UploadRequestHeaders }
export type UploadRequestBody = Buffer | UploadRequestStreamLike

export interface TlsCertRead {
    fingerprint: string
    createdAt: Date
    expiresAt: Date
}

export interface TlsCertWrite {
    key: string
    cert: string
}

export interface StreamStatus<TEnum> {
    title: string
    state: TEnum
}

export interface InputStatus extends StreamStatus<InputOperStatus> {}

export interface OutputStatus extends StreamStatus<OutputOperStatus> {}

export enum InputOperStatus {
    notConfigured = 'notConfigured',
    metricsMissing = 'metricsMissing',
    allOk = 'allOk',
    inputError = 'inputError',
    tr101290Priority1Error = 'tr101290Priority1Error',
    transportError = 'transportError',
    alarm = 'alarm',
    reducedRedundancy = 'reducedRedundancy',
}

export enum OutputOperStatus {
    notConfigured = 'notConfigured',
    metricsMissing = 'metricsMissing',
    tr101290Priority1Error = 'tr101290Priority1Error',
    reducedRedundancy = 'reducedRedundancy',
    allOk = 'allOk',
    // The output is successfully sending data to the configured destination but has not (yet) received an ACK from the receiver
    // Applicable for Rist Simple Profile, but not for e.g. SRT or RTMP since they won't actually send any data until a connection has been established.
    notAcknowledged = 'notAcknowledged',
    inputError = 'inputError',
    outputError = 'outputError', // transport error or appliance alarm
    alarm = 'alarm',
}

export interface BatchDeleteResult {
    deleted: string[]
    notDeleted: string[]
    // This is a hack to make the audit log populate the entity_name column for
    // batch operations with a comma-separated list of names
    names?: string[]
}

export interface SuccessResult {
    success: true
}

export interface CommandReceipt {
    command: {
        id: string
        name: string
    }
}

export type UpgradeCommandReceipt =
    | {
          command: {
              id: string
              name: string
              appliance: string
              updateUrl: string
          }
      }
    | {
          errorMessage: string
      }

export type ApplianceCommandInfoType = Extract<ApplianceCommandType, 'upgrade' | 'upgradeImage' | 'upgradeEdgeOs'>

export interface ApplianceCommandInfo {
    id: string
    name: ApplianceCommandInfoType
    createdAt: Date
    updatedAt: Date
    status: ApplianceCommandStatus
}

export interface ApplianceCommandErrorStatus {
    type: 'error'
    error: string
}

export interface ApplianceCommandSuccessStatus {
    type: 'success'
}

export interface ApplianceCommandInProgressStatus {
    type: 'in-progress'
    progress: number
    status: string
}

export interface ApplianceCommandNotStarted {
    type: 'not-started'
}

export type ApplianceCommandStatus =
    | ApplianceCommandErrorStatus
    | ApplianceCommandSuccessStatus
    | ApplianceCommandInProgressStatus
    | ApplianceCommandNotStarted

export interface ApplianceCommandBase<T extends string> {
    id: string
    name: T
    status: 'not-started' | 'pending' | 'error' | 'success'
}

export interface UpgradeCommand extends ApplianceCommandBase<'upgrade'> {
    name: 'upgrade'
    updateUrl: string
}

export interface UpgradeEdgeOsCommand extends ApplianceCommandBase<'upgradeEdgeOs'> {
    name: 'upgradeEdgeOs'
    updateUrl: string
}

export interface OpenRpcChannelCommand extends ApplianceCommandBase<'openRpcChannel'> {
    name: 'openRpcChannel'
}

export interface RestartEdgeControlCommand
    extends ApplianceCommandBase<'restartEdgeControl'>, RestartEdgeControlArguments {
    name: 'restartEdgeControl'
}

export interface RestartEdgeDataCommand extends ApplianceCommandBase<'restartEdgeData'> {
    name: 'restartEdgeData'
}

export interface ListPcapsCommand extends ApplianceCommandBase<'listPcaps'> {
    name: 'listPcaps'
}

export interface TcpdumpCommand extends ApplianceCommandBase<'tcpdump'>, TcpdumpArguments {
    name: 'tcpdump'
}

export interface UpgradeImageCommand extends ApplianceCommandBase<'upgradeImage'>, UpgradeImageArguments {
    name: 'upgradeImage'
}

export interface PullImageArguments {
    imageVersion: string
}

export interface UpgradeImageArguments {
    imageVersion: string
}

export interface RestartEdgeControlArguments {
    requestedVersion?: string
}

export interface TcpdumpArguments {
    durationSeconds: number
    captureName: string
    friendlyName: string
    protocol: 'udp' | 'tcp'
    srcHost?: string
    srcPort?: number
    dstHost?: string
    dstPort?: number
    /** Bidirectional host filter (captures traffic to AND from this host) */
    host?: string
    /** Bidirectional port filter (captures traffic to AND from this port) */
    port?: number
    iface?: string
}

export interface CollectRistserverCommandLogCommand extends ApplianceCommandBase<'collectRistserverCommandLog'> {
    name: 'collectRistserverCommandLog'
}

export interface CollectRistserverStatsCommand extends ApplianceCommandBase<'collectRistserverStats'> {
    name: 'collectRistserverStats'
}

export type ApplianceCommand =
    | UpgradeCommand
    | OpenRpcChannelCommand
    | RestartEdgeControlCommand
    | RestartEdgeDataCommand
    | ListPcapsCommand
    | TcpdumpCommand
    | UpgradeImageCommand
    | UpgradeEdgeOsCommand
    | CollectRistserverCommandLogCommand
    | CollectRistserverStatsCommand

export type ApplianceCommandType = ApplianceCommand['name']
export type ApplianceCommandInit = OmitFromUnion<ApplianceCommand, 'id' | 'status'>

/** A single ristserver JSON-RPC command and its response, as captured by edge-data's request history. */
export interface RistserverLogEntry {
    /** The JSON-RPC request sent to ristserver. */
    request: { id: number; method: string; params: Record<string, unknown> }
    /** The JSON-RPC response, if one was received. Contains either `result` or `error`. */
    response?: { id: number } & ({ error: { message: string } } | { result: unknown })
    /** ISO 8601 UTC timestamp of when the request was sent. */
    timestamp: string
    /** Milliseconds between request and response. Absent until a response arrives. */
    durationMs?: number
}

/** Result payload of the `collectRistserverCommandLog` appliance command (config commands, stats excluded). */
export interface CollectRistserverCommandLogResult {
    commands: RistserverLogEntry[]
}

/** Result payload of the `collectRistserverStats` appliance command (the latest of each stats command). */
export interface CollectRistserverStatsResult {
    statistics?: RistserverLogEntry
    status?: RistserverLogEntry
    deltaStatistics?: RistserverLogEntry
}

export type RistserverLogKind = 'commands' | 'stats'

/** Metadata for a stored ristserver-log snapshot (list view). */
export interface RistserverLogSnapshot {
    id: string
    applianceId: string
    kind: RistserverLogKind
    /** ISO 8601 UTC timestamp of when the snapshot was collected. */
    createdAt: string
    /** Id of the user that triggered the collection. */
    createdBy: string
    /** Number of entries: commands for 'commands', or present stats responses for 'stats'. */
    entryCount: number
}

/** A stored snapshot together with its full payload (detail / download view). */
export interface RistserverLogSnapshotWithPayload extends RistserverLogSnapshot {
    payload: CollectRistserverCommandLogResult | CollectRistserverStatsResult
}

/** Request body for triggering a ristserver-log collection. */
export interface CollectRistserverLogRequest {
    kind: RistserverLogKind
}

export interface BaseAlarm {
    id: string
    objectId: string
    inputName: string
    instanceId: string
    type: string
    severity: AlarmSeverityLevels
    message: string
    raiseTime: string
    acknowledged: boolean
}

/**
 * @OAS_DESCRIPTION Parameters on which a list of graph nodes can be filtered.
 */
export interface InputGraphFilter extends SearchableFilter {
    input?: string
    output?: string
    inputTr101290Window?: MetricWindow
}

export enum TunnelType {
    // Used by external appliances (edgeConnect, VA, etc) connecting to core nodes.
    // Uses "public" ip mapping.
    external = 1,

    // Used for communication between core appliances (video and thumb) within the same region.
    // Uses "internal" ip mapping.
    internal = 2,

    // Used for communication between different regions.
    // Uses "inter region" ip mapping.
    interRegion = 3,

    // Used for point-to-point communication between appliances
    p2p = 4,
}

export interface TunnelInit {
    /**
     * @DATA_FORMAT UUID
     */
    clientPhysicalPort: string
    /**
     * @DATA_FORMAT IPV4
     */
    clientRemoteIp?: string
    /**
     * @DATA_FORMAT PORT
     */
    clientRemotePort?: number
    /**
     * @DATA_FORMAT UUID
     */
    serverPhysicalPort: string
    /**
     * @DATA_FORMAT PORT
     */
    serverLocalPort?: number
}

export interface Tunnel {
    id: number
    type: TunnelType
    client: string
    clientName: string
    server: string
    serverName: string
    clientLocalIp: string
    clientLocalPort: number
    clientPhysicalPort: { id: string; name: string; applianceOwnerId: string }
    clientRemoteIp: string
    clientRemotePort: number
    serverLocalIp: string
    serverLocalPort: number
    serverPhysicalPort: { id: string; name: string; applianceOwnerId: string }
    // serverPublicIp can potentially be null for internal region thumb instances
    // where no instance metadata is available and no public ip mapping or internal ip mapping
    // is set. We might want to disallow such registrations in the future or disable the appliance
    // until a configuration is provided.
    serverPublicIp: string | null
    inputs: Pick<Input, 'id' | 'name' | 'adminStatus'>[]
    network?: Network
}

export enum UsageDetailType {
    'input' = 'input',
    'output' = 'output',
}

export interface ScaleOperation {
    count: number
    // Time in seconds to wait for status to reflect the update
    waitSeconds?: number
}

export interface ScaleStatus {
    replicas?: number // undefined on deploy
    currentReplicas: number
}

export interface ImageTagOperation {
    tag: string
}

export interface GetImageTagResult {
    container: string
    tag?: string
}

export interface K8SDeployment {
    replicas?: number
    pods: {
        name?: string
        phase?: string
        containers: Array<{
            name: string
            image: string
        }>
    }[]
}

export enum ContainerTerminatedReason {
    completed = 'Completed',
}

export interface ContainerState {
    running?: {
        startedAt?: Date
    }
    terminated?: {
        reason?: ContainerTerminatedReason | string
    }
}

export interface ContainerStatus {
    containerID?: string
    image: string
    imageID: string
    lastState?: object
    name: string
    ready: boolean
    restartCount: number
    state?: ContainerState
}

export interface PodStatus {
    containerStatuses?: Array<ContainerStatus>
}

export interface PvcRequests {
    storage: string
}

export interface PvcResources {
    requests: PvcRequests
}

export interface PvcSpec {
    accessModes: string[]
    resources: PvcResources
    storageClassName: string
    volumeMode: string
    volumeName: string
}

export interface PvcCapacity {
    storage: string
}

export interface PvcStatus {
    accessModes: string[]
    capacity: PvcCapacity
    phase: string
}

export interface Pvc {
    apiVersion: string
    kind: string
    metadata: {
        name: string
        uid: string
        namespace: string
        labels: {
            [key: string]: string
        }
    }
    spec: PvcSpec
    status: PvcStatus
}

export interface PodPersistentVolumeClaim {
    claimName: string
}

export interface Volume {
    name: string
    persistentVolumeClaim?: PodPersistentVolumeClaim
}

export interface Ingress {
    metadata?: {
        uid?: string
        name?: string
        resourceVersion?: string
        namespace?: string
        annotations?: { [key: string]: string }
        labels?: { [key: string]: string }
    }
    spec?: {
        rules?: Array<{
            host?: string // magnus.nimbra.dev
            http?: Array<{
                path?: string // /api
                pathType?: string // Prefix
                backend?: {
                    service?: {
                        name?: string //edge-api
                        port?: { number?: number } // 8080
                    }
                }
            }>
        }>
        tls?: Array<{
            secretName?: string // edge-tls-certificate
            hosts?: string[]
        }>
    }
}

export interface Pod {
    metadata?: {
        generateName?: string
        labels?: {
            // key: 'app.kubernetes.io/name'
            [key: string]: string
        }
        name?: string
    }
    spec?: {
        volumes?: Volume[]
    }
    status?: PodStatus
}

export interface KubernetesNode {
    name: string
    status: string
    externalIP: string
    internalIP: string
    hostname: string
    roles: KubernetesRoles[]
    kubeletVersion?: string
    labels: {
        [key: string]: string
    }
    region: {
        id: string
        name: string
        external: ExternalRegionMode
    }
}

type Gigabyte = number
type CpuUnit = number

export interface KubernetesResourceUsage {
    cpu: {
        capacity: CpuUnit
        usage: CpuUnit
    }
    memory: {
        capacity: Gigabyte
        usage: Gigabyte
    }
    name: string | undefined
}

export interface KubernetesNodeResourceUsage extends KubernetesResourceUsage {
    region: Region['id']
}

export enum ListKubernetesNodeSortableField {
    name = 'name',
    region = 'region',
    status = 'status',
}

export interface KubernetesNodeFilter extends SearchableFilter {
    name?: string
}

export interface UpdateKubernetesNodePayload {
    roles: KubernetesRoles[]
}

export interface CoreSecret {
    secret: string
}

export interface InternalApplianceStatus {
    id: string
    name: string
    status: ApplianceStatus
}

export enum ApplianceRealm {
    core = 'core',
    edge = 'edge',
}

export interface ApplianceTokenInit {
    realm: ApplianceRealm
}

export interface ApplianceTokenCreated extends Entity {
    token: string
    realm: ApplianceRealm
    expiresAt: Date
}

export interface Message<T> {
    type: string
    data: T
}

/**
 * @OAS_DESCRIPTION Data required when creating a new region.
 */
export interface RegionInit {
    name: string
    external?: Exclude<ExternalRegionMode, ExternalRegionMode.core>
}

/**
 * @OAS_DESCRIPTION Region mode
 * * 0 - The core region for Nimbra Edge, there can only be one
 * * 1 - External region, managed by kubernetes. Video/thumb nodes are managed by the kubernetes cluster
 * * 2 - External region, manually managed
 */
export enum ExternalRegionMode {
    core = 0,
    externalK8s = 1,
    external = 2,
}

/**
 * @OAS_DESCRIPTION A region represents an Edge cluster.
 * Appliances have a primary region and an optional secondary region.
 */
export interface Region extends Entity {
    name: string
    default_region: boolean | null
    external: ExternalRegionMode
}

/**
 * @OAS_DESCRIPTION Parameters on which a list of regions can be filtered.
 */
export interface RegionFilter extends SearchableFilter {
    name?: string
    id?: string
    ids?: string[]
    onlyVideoRegions?: boolean
}

export interface RpcSession {
    id: string
    appliance: string
    initializingHost: string
    connectedHost: string
    createdAt: Date
    createdBy: string
    connectedAt: Date
    disconnectedAt: Date
    updatedAt: Date
}

/**
 * @OAS_DESCRIPTION Parameters on which a list of rpc sessions can be filtered.
 */
export interface RpcSessionFilter {
    id?: string
    appliance?: string
}

export interface ThumbHost {
    host: string
    isPrimary: boolean
}

export interface ThumbRoute {
    location: string
    hosts: ThumbHost[]
}

// TODO: Ensure that the oas generator handles mapped type with nullable values
export type NullableApplianceProxySettings = {
    enabled?: ApplianceProxySettings['enabled'] | null
    forwardedCookies?: ApplianceProxySettings['forwardedCookies'] | null
    forwardedHeaders?: ApplianceProxySettings['forwardedHeaders'] | null
}

// setting proxy to null restores the default proxy settings (deletes the overrides)
export type UpdateAppliancePayloadApplianceSettings = Omit<ApplianceSettings, 'proxy'> & {
    proxy?: NullableApplianceProxySettings | null
}

export interface UpdateAppliancePayload {
    ports?: Array<SharedPort>
    region?: RegionReference
    secondaryRegion?: RegionReference | null
    geoLocation?: GeoLocation | null
    logLevel?: LogLevel
    ristserverLogLevel?: RistserverLogLevel
    collectHostMetrics?: boolean
    unhealthyAlarm?: 'on' | 'off' | null
    cordon?: boolean

    /**
     * @OAS_NAME UpdateAppliancePayloadApplianceSettings
     */
    settings?: UpdateAppliancePayloadApplianceSettings

    acceptDisruption?: {
        outputs: string[]
    }

    tags?: MetadataTag['name'][]
}

export interface OutputRecipientList extends Entity {
    name: string
    group: string
    description?: string

    // Only populated when "enriched" in handler listOutputsAndEnrichedOutputRecipientLists()
    _hasOutputsInUse?: boolean
}

export interface OutputRecipientListInit extends Omit<OutputRecipientList, 'id'> {
    addOutputs: string[]
}

export interface OutputRecipientListUpdate extends Omit<OutputRecipientListInit, 'group'> {
    removeOutputs: string[]
}

export interface OutputRecipientInit {
    output: string
    outputRecipientList: string
}

export interface OutputRecipient extends OutputRecipientInit {
    createdAt: Date
}

export type OutputOrRecipientList = Output | OutputRecipientList

export interface SendToOutputRecipientListParams {
    outputRecipientLists: string[]
}

export interface GroupRecipientList extends Entity {
    name: string
    group: string
    description?: string
}

export interface GroupRecipientListInit extends Omit<GroupRecipientList, 'id'> {
    addGroups: string[]
}

export interface GroupRecipientListUpdate extends Omit<GroupRecipientListInit, 'group'> {
    removeGroups: string[]
}

export interface GroupRecipientInit {
    group: string
    groupRecipientList: string
}

export interface GroupRecipient extends GroupRecipientInit, Entity {
    createdAt: Date
}

export enum EntityType {
    alarm = 'alarm',
    appliance = 'appliance',
    alarmLog = 'alarm-log',
    auditLog = 'audit-log',
    distribution = 'distribution',
    input = 'input',
    interface = 'interface',
    group = 'group',
    groupAndRecipientList = 'groupAndRecipientList',
    groupRecipientList = 'groupRecipientList',
    output = 'output',
    outputAndRecipientList = 'outputAndRecipientList',
    outputRecipientList = 'outputRecipientList',
    port = 'port',
    region = 'region',
    user = 'user',
    ipMapping = 'ip-mapping',
    system = 'system',
}

// IDEA: In the future all the objects may use the same property to determine object type
export type GroupOrGroupRecipientList =
    | (Group & { object: EntityType.group })
    | (GroupRecipientList & { object: EntityType.groupRecipientList })

export interface EchoRequest {
    message: string
}

export type GeoAppliance = Required<Pick<BaseAppliance, 'id' | 'name' | 'type' | 'region' | 'geoLocation' | 'owner'>>

export interface GeoApplianceResult {
    connections: Pick<Tunnel, 'id' | 'type' | 'client' | 'server'>[]
    appliances: GeoAppliance[]
}

/// Allowed names of images that may exist in database.
export enum ImageName {
    product = 'product',
    serviceProvider = 'serviceProvider',
    favicon = 'favicon',
}

export type UpdateImageRequest = {
    name: string

    /// image param. May be either:
    /// - the base64 image data directly: 'iVBORw0KGgo...',
    /// - or metadata + base64 image data: 'data:image/png;base64,iVBORw0KGgo...'
    image: string
}

export type ImageDetails = {
    id: string
    name: ImageName
    type: string
}

export interface SanityStatus {
    sane: boolean
}

export interface SleepResult {
    didThrow: boolean
    error: any
}

export enum ListPortSortableField {
    portName = 'portName',
    address = 'address',
    publicAddress = 'publicAddress',
    applianceName = 'applianceName',
    ownerGroupName = 'ownerGroupName',
}

export enum ListAlarmSortableField {
    time = 'time',
    applianceName = 'applianceName',
    alarmCause = 'alarmCause',
    text = 'text',
}

export enum ListUserSortableField {
    userName = 'userName',
    groupName = 'groupName',
    role = 'role',
}

export enum ListApiTokenSortableField {
    name = 'name',
    userName = 'userName',
    groupName = 'groupName',
    role = 'role',
    expiresAt = 'expiresAt',
}

export enum ListInputSortableField {
    inputName = 'inputName',
    applianceName = 'applianceName',
    protocol = 'protocol',
    creationDate = 'creationDate',
    lastModified = 'lastModified',
    accessType = 'accessType',
    ownerGroupName = 'ownerGroupName',
    healthStatus = 'healthStatus',
    numberOfOutputs = 'numberOfOutputs',
}

export enum ListOutputSortableField {
    id = 'id',
    outputName = 'outputName',
    applianceName = 'applianceName',
    protocol = 'protocol',
    creationDate = 'creationDate',
    lastModified = 'lastModified',
    inputName = 'inputName',
    ownerGroupName = 'ownerGroupName',
    healthStatus = 'healthStatus',
}

export type OutputListSortableField = 'name' | 'description'

export enum ListGroupSortableField {
    groupName = 'groupName',
    numberOfUsers = 'numberOfUsers',
    numberOfAppliances = 'numberOfAppliances',
    numberOfPorts = 'numberOfPorts',
}

export type GroupRecipientListSortableField = 'name' | 'description'

export type RegionSortableField = 'id' | 'name' | 'default_region' | 'external'

export enum ApplianceSummarySortableField {
    applianceName = 'applianceName',
    regionName = 'regionName',
    ownerGroupName = 'ownerGroupName',
}

export enum ListApplianceSortableField {
    applianceName = 'applianceName',
    applianceType = 'applianceType',
    applianceVariant = 'applianceVariant',
    regionName = 'regionName',
    secondaryRegionName = 'secondaryRegionName',
    ownerGroupName = 'ownerGroupName',
    lastSeen = 'lastSeen',
    numActiveAlarms = 'numActiveAlarms',
}

export enum ListIpMappingSortableField {
    regionName = 'regionName',
    applianceName = 'applianceName',
    interfaceName = 'interfaceName',
    privateAddress = 'privateAddress',
    publicAddress = 'publicAddress',
    internalAddress = 'internalAddress',
    interRegionAddress = 'interRegionAddress',
}

export interface BillingReport {
    systemName: string
    startDate: Date
    endDate: Date
    periods: BillingPeriod[]
    summary: Billing
}

export interface BillingPeriod extends Billing {
    startDate: Date
    endDate: Date

    maxConcurrencyDate: Date
    details: BillingDetail[]
}

export type BillingDetail = IngressBillingDetail | EgressBillingDetail

export interface BillingDetailBase {
    inputId: string
    inputName: string
    credits: number

    inputServiceCount?: number
}

export interface IngressBillingDetail extends BillingDetailBase {
    inputRedundancy: boolean
    inputMerge: boolean // 2022-7 hitless merge
    inputPortCount: number
    ingressCredits: number
}

export interface EgressBillingDetail extends BillingDetailBase {
    outputId: string
    outputName: string

    outputRedundancy: boolean
    outputPortCount: number
    egressCredits: number
}

export interface Billing {
    ingressCredits: number
    egressCredits: number
    totalCredits: number
}

export interface UsageReport {
    systemName: string
    startDate: Date
    endDate: Date
    ingressTrafficGB: number
    egressTrafficGB: number
    details: UsageDetail[]
    total: number
}

export interface UsageDetail {
    startDate: Date
    endDate: Date | null
    type: UsageDetailType
    inputId: string
    inputName: string
    inputGroup: string
    inputRegion: string
    inputApplianceName: string
    inputSecondaryApplianceName?: string
    inputSecondaryApplianceRegion?: string
    inputIsSmpte_2022_7_Enabled: boolean
    /** @OAS_NAME UsageDetailInputProtocol */
    inputProtocol: PortMode | `${PortMode},${PortMode}`
    inputRedundancyEnabled: boolean
    inputServiceCount?: number
    ingressTrafficGB?: number
    egressTrafficGB?: number
    outputId?: string
    outputName?: string
    outputGroup?: string
    outputRegion?: string
    outputApplianceName?: string
    outputIsSmpte_2022_7_Enabled?: boolean
    outputProtocol?: PortMode
    outputRedundancyEnabled?: boolean
}

export interface ServiceTypeSummary {
    /** @OAS_NAME ServiceType */
    serviceType: ServiceType
    numServices: number
    numRTUs: number
    usageMinutes: number
    ratedMinutes: number
    bytes: bigint
    historicalData: {
        usageMinutes: number[]
        ratedMinutes: number[]
    }
}

interface HistoricalDataRange {
    startDate: Date
    interval: 'day'
    numPoints: number
}

interface UsageReport2Summary {
    historicalDataRange: HistoricalDataRange
    serviceTypes: ServiceTypeSummary[]
}

export interface UsageReport2 {
    systemName: string
    startDate: Date
    endDate: Date
    summary: UsageReport2Summary
    services: ListResult<UsageDetail2>
}

export interface UsageReport2Filter {
    startDate: Date
    endDate: Date
    inputId?: string
    /** @OAS_NAME ServiceType */
    serviceType?: ServiceType
    groupId?: string
    regionId?: string
}

export interface UsageDetail2 {
    /** @OAS_NAME ServiceType */
    serviceType: ServiceType
    serviceName: string
    serviceId: string
    inputId: string
    inputName: string
    inputGroupName: string
    inputRegion: string
    inputRegion2?: string | null
    inputApplianceName: string
    // inputSecondaryApplianceName?: string
    // inputSecondaryApplianceRegion?: string
    /** @OAS_NAME UsageDetailInputProtocol */
    inputProtocol: PortMode | `${PortMode},${PortMode}`
    bytes?: bigint
    usageMinutes: number
    ratedMinutes: number
    outputId?: string
    /** Timestamp of the most recent billed minute for this service */
    lastUsedAt?: Date
    /** True when the service accrued billed minutes within the last few minutes */
    active?: boolean

    group?: {
        id: string
        name: string
    }
    region?: string
    applianceName?: string
    protocol?: PortMode
}

export interface GraphEdge {
    source: string
    target: string
    logicalPortId?: string
    streamId?: number
    channelId?: number
    tunnelData?: GraphEdgeTunnelData
}

export interface GraphEdgeTunnelData {
    tunnelId: number
    streams: Array<{
        channelId: number
        sourceStreamId: number
        sinkStreamId: number
        direction: 'forward' | 'reverse'
    }>
}

export type GraphNodeEntityType = Extract<
    EntityType,
    EntityType.appliance | EntityType.input | EntityType.output | EntityType.port
>

export interface EntityReference {
    id: string
    type: GraphNodeEntityType
}

export interface NodeEntityReference {
    id: string
    type: GraphNodeType
}

export interface GraphNode<TData = GraphNodeData> {
    name: string
    data: TData
}

export interface GraphNodeData extends NodeEntityReference {
    region: string
    secondaryRegion?: string
    meta?: any
}

export type LimitedAppliance = Pick<BaseAppliance, 'id' | 'name' | 'type' | 'region' | 'secondaryRegion'>

export interface DownstreamAppliance extends LimitedAppliance {
    primary?: boolean
}

export interface ServiceOverview {
    graph: InputGraph
    input: Input
    parent?: Input | null
    /**
     * @OAS_NAME ServiceOverviewObjects
     */
    objects: {
        /**
         * @OAS_NAME ServiceOverviewObjectAppliances
         */
        appliances: Array<Appliance | LimitedAppliance>
        outputs: ListResult<Output>
        derivedInputs: Input[]
        tunnels: Tunnel[]
    }
}

export interface InputGraph<TNode = GraphNode, TEdge = GraphEdge> {
    nodes: {
        [name: string]: TNode
    }
    edges: Array<TEdge>
}

export enum GraphNodeType {
    // A logical port of the input we are currently looking at.
    // If it is a parent input, it will have derivedInput(s) in the graph.
    // If it is a derived input, it will have parentInputLogicalPort(s) in the graph.
    inputLogicalPort = 'inputLogicalPort',

    parentInputLogicalPort = 'parentInputLogicalPort',

    // Derived/child input of the (parent) input being inspected.
    derivedInput = 'derivedInput',

    // The edge-appliance where the input resides
    inputEdgeAppliance = 'inputEdgeAppliance',

    // The core-appliance where the input resides
    inputRegionCore = 'inputRegionCore',

    thumbAppliance = 'thumbAppliance',

    // An output
    output = 'output',

    // The core-appliance where the output resides
    outputRegionCore = 'outputRegionCore',

    // The edge-appliance, in the same region as the input, where the output resides
    inputRegionOutputEdgeAppliance = 'inputRegionOutputEdgeAppliance',

    // The edge-appliance, in a different region than the input, where the output resides
    outputRegionOutputEdgeAppliance = 'outputRegionOutputEdgeAppliance',
}

export interface IpMapping {
    /**
     * @DATA_FORMAT IPV4
     */
    privateIp: string
    /**
     * @DATA_FORMAT IPV4_OR_EMPTY_STRING
     */
    publicIp?: string
    /**
     * @DATA_FORMAT IPV4_OR_EMPTY_STRING
     */
    internalIp?: string
    /**
     * @DATA_FORMAT IPV4_OR_EMPTY_STRING
     */
    interRegionIp?: string
}

export enum IpMappingType {
    // A user configured IP mapping
    mapped = 'mapped',

    // A suggested (incomplete) IP mapping suggested by the system
    suggested = 'suggested',
}

export interface IpMappingRead extends IpMapping {
    /**
     * @DATA_FORMAT IPV4
     */
    privateIp: string
    /**
     * @DATA_FORMAT IPV4_OR_EMPTY_STRING
     */
    publicIp?: string
    /**
     * @DATA_FORMAT IPV4_OR_EMPTY_STRING
     */
    internalIp?: string
    /**
     * @DATA_FORMAT IPV4_OR_EMPTY_STRING
     */
    interRegionIp?: string
    region: {
        id: string
        name: string
    }
    nics: LimitedPhysicalPort[]
    type: IpMappingType
}

export interface IpMappingWrite extends IpMapping {
    /**
     * @DATA_FORMAT UUID
     */
    region: string
    /**
     * @DATA_FORMAT IPV4
     */
    privateIp: string
    /**
     * @DATA_FORMAT IPV4_OR_EMPTY_STRING
     */
    publicIp?: string
    /**
     * @DATA_FORMAT IPV4_OR_EMPTY_STRING
     */
    internalIp?: string
    /**
     * @DATA_FORMAT IPV4_OR_EMPTY_STRING
     */
    interRegionIp?: string
}

export interface InputRecipientsUpdate {
    outputRecipientLists?: string[]
    addOutputIds?: string[]
    removeOutputIds?: string[]
}

export interface MetadataTag {
    name: string
    group: Group['id']
}

export interface Network {
    id: string
    name: string
    enabled: boolean
}

export type NetworkInit = Omit<Network, 'id'>

export interface TagFilter extends SearchableFilter {
    groupId?: string
}

export interface NetworkFilter extends SearchableFilter {
    ids?: string[]
    port?: string
}

export enum NetworkSortableField {
    id = 'id',
    name = 'name',
}

export interface Service {
    // Metadata
    id: string
    purpose: string
    product: K8sService
    description?: string
    filterUrl?: string
    allowEnableDisable?: boolean
    allowsVolumeAdministration?: boolean
    allowsRetentionPeriodAdministration?: boolean
    allowsStorageDeletionThresholdAdministration?: boolean
    currentVolumes?: { name: string; currentVolumeCapacity: string }[]

    // Settings
    enabled: boolean
    hidden: boolean
    collectLogs: boolean
    desiredVolumeCapacity?: string
    retentionPeriods?: {
        name: string
        current: number // ms
        desired?: number // ms
    }[]
    storageDeletionThreshold?: number
    storagePools?: {
        name: string
        servers: number
        size: string
    }[]
}

export enum ListServiceSortableField {
    purpose = 'purpose',
    product = 'product',
}

/**
 * @OAS_DESCRIPTION Parameters on which a list of services can be filtered.
 */
export interface ServiceFilter extends SearchableFilter {
    id?: string
    product?: string
    includeHidden?: boolean
}

export type ServiceUpdate = Omit<Service, 'retentionPeriods'> & {
    desiredRetentionPeriod?: NonNullable<Service['retentionPeriods']>[number]['desired']
    desiredStorageDeletionThreshold?: Service['storageDeletionThreshold']
}

export type StatusIssue = {
    name?: string
    reason?: string
    message: string
    timestamp?: Date
}

export type StatusPodIssue = StatusIssue & {
    type?: 'Pending' | 'Failed' | 'Waiting' | 'Terminated'
    path?: string
}

export type StatusResult = {
    status: 'OK' | 'WARN' | 'ERROR'
    description: string
    issues?: (StatusIssue | StatusPodIssue)[]
}

export interface GrafanaDashboard {
    id: number
    uid: string
    title: string
    uri: string
    url: string
    slug: string
    type: string
    tags: string[]
    isStarred: boolean
    sortMeta: number
}

export interface GetUsageReportFilter {
    startDate: Date
    endDate: Date
    groupId?: string
    limit?: number
    skip?: number
    type?: 'ingress' | 'egress'
    format?: 'csv'
}

export interface GetUsageReportFilter2 {
    startDate: Date
    endDate: Date
    groupId?: string
    serviceType?: string
    limit?: number
    skip?: number
    format?: 'csv'
}

export interface RerouteInputPayload {
    /**
     * @DATA_FORMAT UUID
     */
    avoidApplianceId?: string
}

export interface LogBatchMeta {
    timestamp: Date
    container_name?: string // Only optional for backwards compatiblitiy with appliances <=R3.18.0
    edge_component_name?: string // Only optional for backwards compatiblitiy with appliances <=R3.18.0
    edge_service_name?: string // Only optional for backwards compatiblitiy with appliances <=R3.18.0
    /**
     * @deprecated Deprecated since R3.19.0 and not used. Use the fields container_name, edge_component_name and edge_service name instead.
     */
    kubernetes?: {
        /**
         * @deprecated use .edge_component_name instead
         */
        host: string
        /**
         * @deprecated use .container_name instead
         */
        container_name: string
        /**
         * @deprecated
         */
        labels: {
            /**
             * @deprecated use .edge_service_name instead
             */
            ['app_kubernetes_io/name']: string
        }
    }
}

export interface LogBatch {
    meta: LogBatchMeta
    messages: LogMessage[]
}

export type LogMessage = {
    log: string
    logName: string
    level: string
    objects: any[]
    time: Date
    correlationId?: string
    url?: string
}

export enum ACCESS_SCOPES {
    api = 1, // Access to everything user (role) has access to
    read_appliance_summary = 2,
    post_cloud_appliance_audit_log = 3,
    read_api = 4,
}

export type ACCESS_SCOPE = keyof typeof ACCESS_SCOPES
export const ACCESS_SCOPE_NAMES_LIST = Object.keys(ACCESS_SCOPES).filter((key) => isNaN(Number(key))) as ACCESS_SCOPE[]

export const ACCESS_SCOPE_FRIENDLY_NAMES: {
    [key in ACCESS_SCOPE]: string
} = {
    api: 'Full read/write access',
    read_appliance_summary: 'Read appliance summary',
    post_cloud_appliance_audit_log: 'Post cloud appliance audit log',
    read_api: 'Read-only API access',
}

export const ACCESS_SCOPE_DESCRIPTIONS: {
    [key in ACCESS_SCOPE]: string
} = {
    api: 'Access to everything user has access to',
    read_appliance_summary: 'Read appliance summary (id, name, hostname, type, tags, isActive)',
    post_cloud_appliance_audit_log: 'Allow external appliance automation tools to record events in the audit log',
    read_api: 'Read-only access. Cannot create, modify, or delete resources.',
}

export interface VastRequestParams {
    eventId: number
    adBreakId: string
    adPodId: string
}

export interface TrackingEventParams {
    trackingId: string
    errorCode?: string
    playedAt?: Date
}

export enum ListAdPodSortableField {
    name = 'name',
    createdAt = 'createdAt',
}

export enum ListAdCreativeSortableField {
    name = 'name',
    fileSize = 'fileSize',
    duration = 'duration',
    createdAt = 'createdAt',
}

export enum ListAdBreakSortableField {
    startsAt = 'startsAt',
    createdAt = 'createdAt',
}

export enum ListAdAuditSortableField {
    entityType = 'entityType',
    method = 'method',
    status = 'status',
    result = 'result',
    eventId = 'eventId',
    inputId = 'inputId',
    streamId = 'streamId',
    channelId = 'channelId',
    adBreakId = 'adBreakId',
    component = 'component',
    pod = 'pod',
    timestamp = 'timestamp',
    entityId = 'entityId',
    entityName = 'entityName',
    source = 'source',
    target = 'target',
    traceId = 'traceId',
    programNumber = 'programNumber',
}

export enum AdPodStatus {
    pending = 'pending',
    healthy = 'healthy',
    unhealthy = 'unhealthy',
    inactive = 'inactive',
}

export interface AdPod {
    id: string
    name: string
    createdBy: string
    groupId: string
    status: AdPodStatus
    inputIds: string[]
    creatives: AdCreative[]
    createdAt: Date
}

export interface AdPodInit {
    name: string
    inputIds?: string[]
    creatives?: Pick<AdCreative, 'id' | 'filler'>[]
}

export interface AdPodUpdate {
    id: string
    name?: string
    inputIds?: string[]
    creatives?: Pick<AdCreative, 'id' | 'filler'>[]
}

export enum AdCreativeEvent {
    probeOk = 'probeOk',
    probeFailed = 'probeFailed',
    thumbnailOk = 'thumbnailOk',
    thumbnailFailed = 'thumbnailFailed',
    uploadOk = 'uploadOk',
    uploadFailed = 'uploadFailed',
}

export enum AdCreativeStatus {
    pending = 'pending',
    active = 'active',
    thumbnailFailed = 'thumbnailFailed',
    uploadFailed = 'uploadFailed',
    probeFailed = 'probeFailed',
}

export interface AdCreative {
    id: string
    name: string
    createdBy: string
    groupId: string
    status: AdCreativeStatus
    events: AdCreativeEvent[]
    /*
     * Error context if any fail-event is triggered.
     * Such as the reason for a probe failure.
     */
    errorMessage?: string

    /*
     * Metadata about the uploaded file.
     * This is probed after the creation of the ad creative by another service.
     * If {@link status} is "active", the metadata should be available.
     */
    metadata?: {
        format?: {
            durationMs?: number
            bitrate?: number
            fileSize?: number
        }
        video?: {
            codec?: string
            profile?: string
            width?: number
            height?: number
            framerate?: number
            bitrate?: number
        }
        audio?: {
            codec: string
            profile?: string
            channels?: number
            channelLayout?: string
            bitrate?: number
        }
    }
    /*
     * Users are allowed to override the duration of the ad creative.
     * If this field is set, it should be used instead of {@link durationMs}.
     */
    userDurationMs?: number

    /*
     * The path to the file in S3.
     * A file might not exist in S3 if the upload failed or if the client is slow.
     */
    filepath: string
    /*
     * The path to the thumbnail in S3
     * A file might not exist in S3 if the upload failed or if the client is slow.
     */
    thumbnailFilepath: string
    createdAt: Date

    /*
     * A filler ad creative represents an ad in a pod whose tracking events are not
     * required to be tracked. This field is used for ad creatives that are inside
     * an ad pod.
     */
    filler?: boolean
}

export type AdCreativeWithUrls = {
    downloadUrl: string
    uploadUrl: string
    thumbnailUrl: string
} & AdCreative

export interface AdCreativeInit {
    name: string
}

export interface AdCreativeUpdate {
    id: string
    name?: string
    durationMs?: number
}

export interface AdCreativeEventInit {
    type: AdCreativeEvent
}

export enum AdBreakStatus {
    'pending' = 'pending',
    'playing' = 'playing',
    'playOk' = 'playOk',
    'playFailed' = 'playFailed',
    'collectingStats' = 'collectingStats',
    'aborted' = 'aborted',
}

export interface AdBreak {
    id: string
    groupId: string
    status: AdBreakStatus
    errorMessage?: string
    eventId: number
    programNumber?: number
    programName?: string
    inputId: string
    playlist?: Partial<ApplianceAdPlaylist>
    trackingEvents: (AdTracking | AdErrorTracking)[]
    createdAt: Date

    startsAt: Date
    /**
     * Field is set when a SCTE 35 notification includes an end timestamp. It is the requested
     * end time of the ad break, which may be different from the actual end time.
     */
    endsAt?: Date
    /**
     * Field is set when the ad player reports that the ad break has ended.
     * It is primarily used to determine the end time of an ad break has ended when its SCTE 35
     * notification did not include an end timestamp.
     *
     * It can also be used as an "actual end time" of the ad break, compared to
     * the requested end time that {@link endsAt} holds.
     */
    endedAt?: Date

    adPodId?: string
    vastXml?: string
}

export interface AdBreakInit {
    id: string
    groupId: string
    eventId: number
    programNumber?: number
    programName?: string
    inputId: string
    startsAt: Date
    endsAt?: Date
    adPodId?: string
}

export interface AdTracking {
    id: string
    adCreativeId: string
    podIndex: number
    eventId: number
    eventType: string
    reportedAt?: Date
    playedAt?: Date
    reportCount: number
}

export type AdErrorTracking = AdTracking & {
    errorCode: string
    errorMessage?: string
}

export type AdStorageStatus = {
    buckets: {
        ads: {
            ads: {
                size: number
                count: number
            }
            mezzanines: {
                size: number
                count: number
            }
        }
        adServer: {
            creatives: {
                size: number
                count: number
            }
            thumbnails: {
                size: number
                count: number
            }
        }
    }
    total: {
        size: number
        count: number
    }
}

export interface BundleDetails {
    bundleNames: string[]
}

export interface BundleVersions {
    versions: string[]
    details: Record<string, BundleDetails>
}

export interface AdServer {
    id: string
    name: string
    baseUrl: string
    createdAt: string
}

export interface AdServerInit {
    name: string
    baseUrl: string
}

export interface AdServerUpdate {
    id: string
    name: string
    baseUrl: string
}

export type DeletedAdServer = Entity
